import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Flame, Coins, Gift, Calendar, Clock, Check, X, Sparkles, ShieldCheck,
  ShoppingBag, ChevronRight, History, Ticket, Package as PkgIcon, Send, Bell, MessageCircle,
  MapPin, Phone, Heart, Bookmark, Repeat, Trash2, BellRing, Download, Wallet, FileText, Utensils, Search, AlertTriangle, Printer, Gamepad2, ExternalLink, CreditCard, CheckCircle2, Newspaper, QrCode, Star, Home as HomeIcon, MoreHorizontal, Camera, Store, Truck, HelpCircle,
} from "lucide-react";
import { CollectionBalances, CollectionTabs } from "../components/CollectionBalances";
import ProximityWatcher from "../components/ProximityWatcher";
import { buildReceiptHtml, openReceiptPrintWindow } from "../lib/receipt";
import CustomerAiChat from "../components/CustomerAiChat";
import { GiftFromPoints } from "../components/GiftFromPoints";
import CashbackCard from "../components/CashbackCard";
import InitiativesHome from "../components/InitiativesHome";
import CatalogHome from "../components/CatalogHome";
import PickupQr from "../components/PickupQr";
import { CustomerReview } from "../components/CustomerReview";
import AddressMap from "../components/AddressMap";
import DailyProductCard from "../components/DailyProductCard";
import HowItWorksPanel from "../components/HowItWorksPanel";
import { VideoPlayer } from "../lib/video";
import { resolveImageUrl } from "../context/AppSettingsContext";
import { PublicShell, BannerCarousel, SoftTile, AreaTile, SectionTitle, PromoBanner, useBrand, useTiles, MENU_SECTIONS } from "./PublicHome";

const tenantHeaders = () => { const t = localStorage.getItem("kl_tenant"); return t ? { headers: { "X-Tenant": t } } : {}; };
const TAB_TITLES = {
  flyer: "Volantino", menu: "Prodotti", history: "Movimenti", coupon: "I tuoi coupon", rewards: "Premi", orders: "Ordini",
  branch: "La mia sede", referral: "Invita un amico", privacy: "Privacy", news: "News", concorsi: "Concorsi", chat: "Chat", guida: "Come funziona",
};
const PERSONAL_TILES = [
  { k: "history", l: "Movimenti", icon: History }, { k: "coupon", l: "Coupon", icon: Ticket }, { k: "rewards", l: "Premi", icon: Gift },
  { k: "orders", l: "Ordini", icon: ShoppingBag }, { k: "flyer", l: "Volantino", icon: FileText }, { k: "menu", l: "Prodotti", icon: Utensils },
  { k: "branch", l: "La mia sede", icon: MapPin }, { k: "news", l: "News", icon: Bell }, { k: "referral", l: "Invita", icon: Send },
  { k: "concorsi", l: "Concorsi", icon: Gamepad2 }, { k: "chat", l: "Chat", icon: MessageCircle }, { k: "guida", l: "Come funziona", icon: HelpCircle }, { k: "privacy", l: "Privacy", icon: ShieldCheck },
];
const PUBLIC_TILE_KEYS = ["punti-vendita", "offerte", "iniziative"];
const QUICK_KEYS = ["history", "rewards", "coupon", "branch"];
// modulo del circuito (Super-Admin) che abilita ogni sezione personale
const TILE_MODULE = { coupon: "coupons", orders: "orders", flyer: "flyers", concorsi: "contest", referral: "referral", rewards: "reservations" };
const tilesOn = (modules) => PERSONAL_TILES.filter((t) => !TILE_MODULE[t.k] || modules?.[TILE_MODULE[t.k]] !== false);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = (card) => `otp_session_${card}`;

function resolveImg(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

/* ---------------- PWA + Push helpers ---------------- */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register("/sw.js");
    return reg;
  } catch (e) {
    console.warn("SW register failed", e);
    return null;
  }
}

async function ensurePushSubscription(card, sessionToken) {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return null;
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (sub) return sub;
  if (Notification.permission === "denied") return null;
  const perm = Notification.permission === "granted"
    ? "granted"
    : await Notification.requestPermission();
  if (perm !== "granted") return null;
  try {
    const { data } = await axios.get(`${API}/public/push/vapid-public-key`);
    if (!data.key) return null;
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.key),
    });
    await axios.post(`${API}/public/push/subscribe`, {
      card_number: card,
      session_token: sessionToken,
      subscription: sub.toJSON(),
    });
    return sub;
  } catch (e) {
    console.warn("Push subscribe failed", e);
    return null;
  }
}

function formatErr(err) {
  const d = err?.response?.data?.detail;
  if (!d) return "Errore imprevisto";
  return typeof d === "string" ? d : JSON.stringify(d);
}

function loadSession(card) {
  try {
    const raw = localStorage.getItem(SESSION_KEY(card));
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s.session_token || new Date(s.expires_at) < new Date()) return null;
    return s;
  } catch { return null; }
}

function filterOrdersByRange(orders, range) {
  if (!orders || !range || range.preset === "all") return orders || [];
  const now = new Date();
  let fromMs = 0;
  let toMs = Infinity;
  if (range.preset === "week") fromMs = now.getTime() - 7 * 24 * 60 * 60 * 1000;
  else if (range.preset === "month") fromMs = now.getTime() - 30 * 24 * 60 * 60 * 1000;
  else if (range.preset === "year") fromMs = now.getTime() - 365 * 24 * 60 * 60 * 1000;
  else if (range.preset === "custom") {
    if (range.from) fromMs = new Date(range.from + "T00:00:00").getTime();
    if (range.to) toMs = new Date(range.to + "T23:59:59").getTime();
  }
  return orders.filter((o) => {
    const t = new Date(o.created_at).getTime();
    return t >= fromMs && t <= toMs;
  });
}

function OrdersRangeSelector({ value, onChange }) {
  const presets = [
    { k: "all", label: "Tutti" },
    { k: "week", label: "Ultima settimana" },
    { k: "month", label: "Ultimo mese" },
    { k: "year", label: "Ultimo anno" },
    { k: "custom", label: "Personalizzato" },
  ];
  return (
    <div className="flex flex-col gap-1.5 w-full sm:w-auto" data-testid="public-orders-range">
      <div className="flex gap-1 flex-wrap">
        {presets.map((p) => {
          const on = value.preset === p.k;
          return (
            <button
              key={p.k}
              type="button"
              onClick={() => onChange({ ...value, preset: p.k })}
              data-testid={`public-orders-range-${p.k}`}
              className={`text-[10px] px-2.5 py-1 rounded-full border transition ${on ? "bg-amber-500/20 text-amber-300 border-amber-500/50" : "border-zinc-800 text-zinc-500 hover:border-zinc-600"}`}
            >
              {p.label}
            </button>
          );
        })}
      </div>
      {value.preset === "custom" && (
        <div className="flex gap-1.5 items-center">
          <input
            type="date"
            value={value.from}
            onChange={(e) => onChange({ ...value, from: e.target.value })}
            data-testid="public-orders-range-from"
            className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-[11px] text-zinc-300"
          />
          <span className="text-[10px] text-zinc-500">→</span>
          <input
            type="date"
            value={value.to}
            onChange={(e) => onChange({ ...value, to: e.target.value })}
            data-testid="public-orders-range-to"
            className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-[11px] text-zinc-300"
          />
        </div>
      )}
    </div>
  );
}

export default function PublicPortal() {
  const { card } = useParams();
  const [session, setSession] = useState(() => loadSession(card));
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [reserving, setReserving] = useState(null);
  const [orderOpen, setOrderOpen] = useState(false);
  const [paypalOn, setPaypalOn] = useState(false);
  const [paypalTest, setPaypalTest] = useState(false);
  const [payingOrder, setPayingOrder] = useState(null);
  useEffect(() => { axios.get(`${API}/public/paypal/status`, tenantHeaders()).then(({ data: s }) => { setPaypalOn(!!s.enabled); setPaypalTest(!!s.enabled && s.mode === "sandbox"); }).catch(() => {}); }, []);
  const payOrderPayPal = async (orderId) => {
    setPayingOrder(orderId);
    try {
      const { data: r } = await axios.post(`${API}/public/orders/${orderId}/paypal/create`, { card_number: card, session_token: session.session_token, origin_url: window.location.origin });
      window.location.href = r.approve_url;
    } catch (err) { toast.error(err?.response?.data?.detail || "Pagamento non avviato"); setPayingOrder(null); }
  };
  const [verifyingOrder, setVerifyingOrder] = useState(null);
  const verifyOrderPayPal = async (orderId) => {
    setVerifyingOrder(orderId);
    try {
      const { data: r } = await axios.post(`${API}/public/orders/${orderId}/paypal/verify`, { card_number: card, session_token: session.session_token });
      if (r.state === "completed") { toast.success(r.already ? "Pagamento già registrato" : "Pagamento PayPal confermato. Grazie!"); load(); }
      else if (r.state === "needs_approval" && r.approve_url) toast.warning(r.detail, { action: { label: "Apri PayPal", onClick: () => { window.location.href = r.approve_url; } }, duration: 10000 });
      else toast.error(r.detail || "Pagamento non confermato");
    } catch (err) { toast.error(err?.response?.data?.detail || "Verifica non riuscita"); } finally { setVerifyingOrder(null); }
  };
  // Carrello preparato dalla landing (/scopri): all'arrivo nel portale lo recupero nel modulo ordine
  useEffect(() => {
    if (!session || !data) return;
    try {
      const cart = JSON.parse(localStorage.getItem("kl_cart") || "null");
      const sp = new URLSearchParams(window.location.search);
      if (cart?.items?.length && (sp.get("cart") === "1" || cart.src === "scopri")) {
        setOrderInitialItem({ items: cart.items.map((i) => ({ name: i.name, qty: i.qty, price: i.price })), source: "scopri" });
        setTab("orders"); setOrderOpen(true);
        toast.success(`Carrello recuperato: ${cart.items.length} prodotti`);
        localStorage.removeItem("kl_cart");
        if (sp.get("cart")) window.history.replaceState(null, "", window.location.pathname);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!session, !!data]);

  // Ritorno da PayPal: /carta/:card?paypal=success&order=<id>&token=<paypalOrderId>
  useEffect(() => {
    if (!session) return;
    const sp = new URLSearchParams(window.location.search);
    const st = sp.get("paypal"), orderId = sp.get("order"), token = sp.get("token");
    if (!st) return;
    window.history.replaceState({}, "", window.location.pathname);
    if (st === "success" && orderId && token) {
      setTab("orders");
      axios.post(`${API}/public/orders/${orderId}/paypal/capture`, { card_number: card, session_token: session.session_token, paypal_order_id: token })
        .then(() => { toast.success("Pagamento PayPal completato. Grazie!"); load(); })
        .catch((err) => toast.error(err?.response?.data?.detail || "Pagamento non confermato"));
    } else if (st === "cancel") { setTab("orders"); toast.info("Pagamento PayPal annullato: puoi pagare al ritiro"); }
    // eslint-disable-next-line
  }, [session]);
  const [galleryReward, setGalleryReward] = useState(null);
  const [collTab, setCollTab] = useState(null);
  const [reserveOption, setReserveOption] = useState("points");
  const [tab, setTab] = useState("card");
  const { brand, settings } = useBrand();
  const { apply: applyTiles } = useTiles();
  const [banners, setBanners] = useState([]);
  const [showQr, setShowQr] = useState(false);
  const [unread, setUnread] = useState(0);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarBroken, setAvatarBroken] = useState(false);
  const avatarInputRef = useRef(null);
  const refreshUnread = async () => {
    if (!session) return;
    try {
      const { data: n } = await axios.get(`${API}/public/notifications`, { params: { card_number: card, session_token: session.session_token } });
      setUnread(n.unread || 0);
    } catch { /* silenzioso */ }
  };
  useEffect(() => { refreshUnread(); /* eslint-disable-next-line */ }, [session, tab]);
  const removeAvatar = async () => {
    if (!window.confirm("Rimuovere la foto profilo? Tornerà visibile l'iniziale del nome.")) return;
    setAvatarBusy(true);
    try {
      await axios.delete(`${API}/public/customer/${encodeURIComponent(card)}/avatar`, { params: { session_token: session.session_token } });
      toast.success("Foto profilo rimossa");
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Operazione non riuscita"); }
    finally { setAvatarBusy(false); }
  };
  const uploadAvatar = async (file) => {
    if (!file) return;
    setAvatarBusy(true);
    try {
      const fd = new FormData();
      fd.append("session_token", session.session_token);
      fd.append("file", file);
      await axios.post(`${API}/public/customer/${encodeURIComponent(card)}/avatar`, fd);
      toast.success("Foto profilo aggiornata");
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Caricamento non riuscito"); }
    finally { setAvatarBusy(false); if (avatarInputRef.current) avatarInputRef.current.value = ""; }
  };
  useEffect(() => { axios.get(`${API}/public/banners`, tenantHeaders()).then(({ data }) => setBanners(data || [])).catch(() => {}); }, []);
  const openTab = (k) => { setTab(k); if (k === "concorsi") trackContest("open"); window.scrollTo({ top: 0 }); };
  const [menuCat, setMenuCat] = useState("__ALL__");
  const [menuSearch, setMenuSearch] = useState("");
  const [productDetail, setProductDetail] = useState(null);
  const [orderInitialItem, setOrderInitialItem] = useState(null);
  const [ordersRange, setOrdersRange] = useState({ preset: "all", from: "", to: "" });
  const [ordersSearch, setOrdersSearch] = useState("");
  const [ordersStatusFilter, setOrdersStatusFilter] = useState("all"); // all|received|in_prep|ready|delivered|cancelled
  const [ordersReverse, setOrdersReverse] = useState(false);
  const [cancelWindow, setCancelWindow] = useState(10);
  useEffect(() => {
    axios.get(`${API}/public/orders/cancel-window`)
      .then(({ data }) => setCancelWindow(data.cancel_window_minutes || 10))
      .catch(() => {});
  }, []);
  const [favorites, setFavorites] = useState(() => {
    try {
      const raw = localStorage.getItem(`menu_favs_${card}`);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  const toggleFavorite = (pid) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      try { localStorage.setItem(`menu_favs_${card}`, JSON.stringify([...next])); } catch {}
      return next;
    });
  };
  const openMenuOrder = (product, qty = 1) => {
    setOrderInitialItem(product ? { name: product.name, qty: Math.max(1, parseInt(qty) || 1), price: product.price } : null);
    setOrderOpen(true);
  };
  const [flyers, setFlyers] = useState([]);
  const [branches, setBranches] = useState([]);
  // Ristampa scontrino cliente (copia)
  const [reprintOpen, setReprintOpen] = useState(false);
  const [reprintHtml, setReprintHtml] = useState("");
  const [reprintCtx, setReprintCtx] = useState(null);
  const [reprintLoading, setReprintLoading] = useState(false);

  // Sintetico oggetto settings per lo scontrino (usa lo snapshot che il portale ha già)
  const receiptSettings = () => ({
    logo_url: data?.brand?.logo_url,
    primary_color: data?.brand?.primary_color,
    accent_color: data?.brand?.accent_color,
    app_name: data?.brand?.app_name || "Loyalty",
    card_footer: data?.brand?.card_footer,
  });

  const openReprint = async (tx) => {
    if (!session) { toast.error("Sessione scaduta, effettua nuovamente il login"); return; }
    setReprintOpen(true);
    setReprintLoading(true);
    setReprintHtml("");
    setReprintCtx(null);
    try {
      const { data: ctx } = await axios.get(
        `${API}/public/customer/${encodeURIComponent(card)}/transactions/${tx.id}/receipt-context`,
        { params: { session_token: session.session_token } }
      );
      setReprintCtx(ctx);
      const html = buildReceiptHtml({
        earn: ctx.earn, customer: ctx.customer, branch: ctx.branch,
        settings: receiptSettings(),
        multiplier: ctx.multiplier || 1,
        activeCoupons: ctx.active_coupons || [],
        duplicate: true, originalDate: ctx.original_created_at,
        autoPrint: false,
      });
      setReprintHtml(html);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile ristampare");
      setReprintOpen(false);
    } finally { setReprintLoading(false); }
  };
  const doReprint = () => {
    if (!reprintCtx) return;
    openReceiptPrintWindow({
      earn: reprintCtx.earn, customer: reprintCtx.customer, branch: reprintCtx.branch,
      settings: receiptSettings(),
      multiplier: reprintCtx.multiplier || 1,
      activeCoupons: reprintCtx.active_coupons || [],
      duplicate: true, originalDate: reprintCtx.original_created_at,
    });
    setReprintOpen(false);
  };
  const [branchBusy, setBranchBusy] = useState(false);
  const [pdfViewer, setPdfViewer] = useState(null); // { title, url }
  // Coupon → QR modal (cliente mostra al negozio per lettura)
  const [couponQr, setCouponQr] = useState(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  // Tick every 60s to refresh coupon countdown labels
  useEffect(() => {
    const iv = setInterval(() => setNowTick(Date.now()), 60_000);
    return () => clearInterval(iv);
  }, []);

  // OTP state
  const [otpSending, setOtpSending] = useState(false);
  const [otpSent, setOtpSent] = useState(null);
  const [otpCode, setOtpCode] = useState("");
  const [otpVerifying, setOtpVerifying] = useState(false);

  const lostCard = async () => {
    try {
      await axios.post(`${API}/public/card/block`, { card_number: card, session_token: session.session_token, reason: "lost" });
      toast.success("Carta bloccata. Per la sostituzione rivolgiti al punto vendita.");
      logout();
    } catch (err) { toast.error(err?.response?.data?.detail || "Operazione non riuscita"); }
  };

  const load = async () => {
    if (!session) return;
    try {
      const { data } = await axios.get(
        `${API}/public/customer/${encodeURIComponent(card)}/full`,
        { params: { session_token: session.session_token } }
      );
      setData(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(SESSION_KEY(card));
        setSession(null);
      } else {
        setNotFound(true);
      }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [card, session?.session_token]);

  // Reset avatar broken flag when the avatar URL changes
  useEffect(() => { setAvatarBroken(false); }, [data?.customer?.avatar_url, data?.customer?.avatar_updated_at]);

  // Load flyers (branch-scoped) once the customer data is available.
  useEffect(() => {
    if (!data?.customer) return;
    const branchId = data.customer.branch_id;
    const q = branchId ? `?branch_id=${encodeURIComponent(branchId)}` : "";
    axios.get(`${API}/public/flyers${q}`)
      .then((res) => setFlyers(res.data || []))
      .catch(() => setFlyers([]));
  }, [data?.customer?.id, data?.customer?.branch_id]);

  // Load the full branch list so the user can switch preferred branch
  useEffect(() => {
    axios.get(`${API}/public/branches`)
      .then((r) => setBranches(r.data || []))
      .catch(() => setBranches([]));
  }, []);

  const changeBranch = async (newBranchId) => {
    if (branchBusy) return;
    setBranchBusy(true);
    try {
      await axios.post(`${API}/public/customer/branch`, {
        session_token: session.session_token,
        card_number: card,
        branch_id: newBranchId || null,
      });
      toast.success(newBranchId ? "Sede aggiornata" : "Preferenza sede rimossa");
      await load(); // refresh customer/branch/flyers
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Aggiornamento fallito");
    } finally { setBranchBusy(false); }
  };

  // Register SW once (on mount) and subscribe to push once we have a session
  useEffect(() => { registerServiceWorker(); }, []);
  useEffect(() => {
    if (session?.session_token) {
      ensurePushSubscription(card, session.session_token).catch(() => null);
    }
  }, [card, session?.session_token]);

  const [pushBusy, setPushBusy] = useState(false);
  const [walletStatus, setWalletStatus] = useState({ apple: false, google: false });
  const [walletBusy, setWalletBusy] = useState(false);
  // A/B test variant: assigned once per browser, kept in localStorage
  const [walletVariant] = useState(() => {
    try {
      let v = localStorage.getItem("wallet_ab_variant");
      if (v !== "icon" && v !== "long") {
        v = Math.random() < 0.5 ? "icon" : "long";
        localStorage.setItem("wallet_ab_variant", v);
      }
      return v;
    } catch { return "long"; }
  });
  useEffect(() => {
    axios.get(`${API}/public/wallet/status`).then(({ data }) => setWalletStatus(data)).catch(() => null);
  }, []);
  const enablePush = async () => {
    setPushBusy(true);
    try {
      const sub = await ensurePushSubscription(card, session.session_token);
      if (sub) toast.success("Notifiche push attivate ✓");
      else if (Notification.permission === "denied") toast.error("Permesso notifiche bloccato dal browser");
      else toast.error("Impossibile attivare le notifiche");
    } finally { setPushBusy(false); }
  };

  const testPush = async () => {
    setPushBusy(true);
    try {
      await axios.post(`${API}/public/push/test`, {
        card_number: card, session_token: session.session_token,
      });
      toast.success("📱 Push di prova inviata — controlla le notifiche!", { duration: 4000 });
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setPushBusy(false); }
  };

  const trackContest = async (event) => {
    try {
      const { data } = await axios.post(`${API}/public/contest/track`, {
        card_number: card, session_token: session.session_token, event,
      });
      return data;
    } catch (_) { return null; }
  };
  const [contestBonus, setContestBonus] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [contestHistory, setContestHistory] = useState([]);
  useEffect(() => {
    if (tab !== "concorsi" || !data?.brand?.contest_url || !session?.session_token) return;
    const p = { params: { card_number: card, session_token: session.session_token } };
    axios.get(`${API}/public/contest/bonus-status`, p).then((r) => setContestBonus(r.data)).catch(() => {});
    axios.get(`${API}/public/contest/leaderboard`, p).then((r) => setLeaderboard(r.data)).catch(() => {});
    axios.get(`${API}/public/contest/history`, p).then((r) => setContestHistory(r.data || [])).catch(() => {});
  }, [tab, data?.brand?.contest_url, card, session?.session_token]);
  const completeContest = async () => {
    const r = await trackContest("complete");
    if (r?.awarded) { toast.success(`🎉 +${r.bonus_points} punti per la partecipazione!`); setContestBonus((b) => ({ ...(b || {}), claimed_today: true })); load?.(); }
    else if (r?.reason === "already_today") toast.info("Bonus già ricevuto oggi: torna domani!");
    else toast.success("🎉 Grazie! Partecipazione registrata.");
    axios.get(`${API}/public/contest/leaderboard`, { params: { card_number: card, session_token: session.session_token } }).then((x) => setLeaderboard(x.data)).catch(() => {});
  };

  const addToWallet = async () => {
    setWalletBusy(true);
    try {
      const ua = navigator.userAgent || "";
      const isApple = /iPad|iPhone|iPod|Mac/.test(ua) && !/Android/.test(ua);
      const isAndroid = /Android/i.test(ua);
      const platform = isApple ? "ios" : isAndroid ? "android" : "desktop";
      const method = (isApple && walletStatus.apple) || (!walletStatus.google && walletStatus.apple)
        ? "apple" : "google";
      // Track first, so even if user leaves the page the analytics event is stored
      axios.post(`${API}/public/wallet/track`, {
        card_number: card, platform, method, variant: walletVariant, user_agent: ua.slice(0, 300),
      }).catch(() => null);
      if (isApple && walletStatus.apple) {
        window.location.href = `${API}/public/wallet/${encodeURIComponent(card)}/apple.pkpass`;
      } else if (walletStatus.google) {
        const { data } = await axios.get(`${API}/public/wallet/${encodeURIComponent(card)}/google-link`);
        if (data?.url) window.location.assign(data.url);
        else toast.error("Link Wallet non disponibile");
      } else if (walletStatus.apple) {
        window.location.href = `${API}/public/wallet/${encodeURIComponent(card)}/apple.pkpass`;
      } else {
        toast.error("Wallet non ancora configurato dall'admin");
      }
    } catch (err) { toast.error(formatErr(err)); }
    finally { setWalletBusy(false); }
  };
  // Alternativa senza Apple/Google Wallet: salva l'immagine della card (QR incluso) + suggerimento "Aggiungi alla schermata Home"
  const [saveCardOpen, setSaveCardOpen] = useState(false);
  const saveCardImage = () => {
    axios.post(`${API}/public/wallet/track`, { card_number: card, platform: /Android/i.test(navigator.userAgent) ? "android" : /iPhone|iPad|iPod/.test(navigator.userAgent) ? "ios" : "desktop", method: "image", variant: walletVariant, user_agent: (navigator.userAgent || "").slice(0, 300) }).catch(() => null);
    window.location.href = `${API}/public/wallet/${encodeURIComponent(card)}/card.png?session_token=${encodeURIComponent(session.session_token)}`;
  };

  const sendOtp = async () => {
    setOtpSending(true);
    try {
      const { data } = await axios.post(`${API}/public/otp/send`, { card_number: card });
      setOtpSent(data);
      const where = [data.channels?.includes("whatsapp") && `WhatsApp ${data.phone_masked || ""}`, data.channels?.includes("email") && `email ${data.email_masked || ""}`].filter(Boolean).join(" e ");
      toast.success(`Codice inviato via ${where}`);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setOtpSending(false); }
  };

  const verifyOtp = async () => {
    if (!otpCode.trim()) return toast.error("Inserisci il codice ricevuto");
    setOtpVerifying(true);
    try {
      const { data } = await axios.post(`${API}/public/otp/verify`, {
        card_number: card, code: otpCode.trim(),
      });
      localStorage.setItem(SESSION_KEY(card), JSON.stringify(data));
      setSession(data);
      setOtpCode("");
      setOtpSent(null);
      toast.success("Autenticato ✓");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setOtpVerifying(false); }
  };

  // Password-based login state (sostituisce l'OTP per l'accesso via /carta/:card)
  const [pwd, setPwd] = useState("");
  const [pwdBusy, setPwdBusy] = useState(false);

  const doPasswordLogin = async () => {
    if (!pwd.trim()) return toast.error("Inserisci la password");
    setPwdBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/password/login`, {
        card_number: card, password: pwd,
      });
      if (!data.verified || !data.session_token) throw new Error("Password errata");
      localStorage.setItem(SESSION_KEY(card), JSON.stringify(data));
      setSession(data);
      setPwd("");
      toast.success("Autenticato ✓");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setPwdBusy(false); }
  };

  const logout = () => {
    localStorage.removeItem(SESSION_KEY(card));
    setSession(null);
    setData(null);
  };

  // OTP screen
  if (notFound) {
    return (
      <PublicShell title="Carta" back="/cliente/accedi" testid="public-card-notfound">
        <div className="rounded-3xl bg-white p-8 text-center shadow-sm ring-1 ring-zinc-200">
          <X className="h-8 w-8 text-red-500 mx-auto mb-3" />
          <div className="text-lg font-heading font-bold">Carta non trovata</div>
        </div>
      </PublicShell>
    );
  }

  if (!session) {
    return (
      <PublicShell title="Accedi" back="/cliente/accedi" testid="public-password-page">
        <div className="client-light rounded-3xl bg-white p-6 space-y-4 shadow-sm ring-1 ring-zinc-200">
          <div className="text-center">
            <div className="h-12 w-12 rounded-2xl mx-auto flex items-center justify-center" style={{ background: brand, color: "#fff" }}>
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div className="font-heading text-xl font-bold mt-3">Accedi alla tua carta</div>
            <div className="text-sm text-zinc-400 mt-1">Inserisci la password scelta in fase di registrazione</div>
          </div>
          <div className="text-sm bg-zinc-950 border border-zinc-800 rounded-lg p-3">
            Carta: <span className="font-mono font-semibold" style={{ color: brand }}>{card}</span>
          </div>
          <div>
            <Label className="text-zinc-300 mb-1 inline-block text-xs">Password</Label>
            <Input
              data-testid="public-password-input"
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              className="bg-zinc-950 border-zinc-800 h-12"
              placeholder="••••••••"
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && doPasswordLogin()}
            />
          </div>
          <Button
            data-testid="public-password-submit"
            onClick={doPasswordLogin}
            disabled={pwdBusy}
            className="w-full h-12 text-white font-semibold hover:opacity-90"
            style={{ background: brand }}
          >
            {pwdBusy ? "Accesso…" : "Accedi"}
          </Button>
          <div className="text-center text-[11px] text-zinc-500">
            Password dimenticata? <a href={`/cliente/accedi?card=${card}`} className="underline" style={{ color: brand }}>Recupera dal login principale</a>
          </div>
        </div>
      </PublicShell>
    );
  }

  if (!data) return <PublicShell testid="public-portal-loading"><p className="text-sm text-zinc-500 text-center py-10">Caricamento…</p></PublicShell>;

  const c = data.customer;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(c.card_number)}`;

  const reserve = async () => {
    const cost = reserving.redemption_mode === "both" && reserveOption === "cash" ? (reserving.points_cost_cash || reserving.points_cost) : reserving.points_cost;
    if (!window.confirm(`Confermi la prenotazione di "${reserving.name}" per ${cost} pt${reserving.redemption_mode === "both" && reserveOption === "cash" ? ` + € ${Number(reserving.contribution_eur || 0).toFixed(2)}` : ""}?`)) return;
    try {
      await axios.post(`${API}/public/reservations`, {
        card_number: card,
        reward_id: reserving.id,
        option: reserving.redemption_mode === "both" ? reserveOption : undefined,
      });
      setReserving(null);
      toast.success("Prenotazione inviata 🎉");
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const requestPickup = async (r) => {
    if (!window.confirm(`Richiedere il ritiro di "${r.reward_name}"?\nIl negozio riceverà la notifica.`)) return;
    try {
      await axios.post(`${API}/public/reservations/${r.id}/pickup`, {
        card_number: card, session_token: session.session_token,
      });
      toast.success("Ritiro richiesto ✅");
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const activeCoupons = (data.coupons || []).filter((cp) => cp.active_now).length;
  const openCouponQr = (cp) => {
    setCouponQr(cp);
    axios.post(`${API}/public/notifications/coupon-read?coupon_id=${encodeURIComponent(cp.id)}`, { card_number: card, session_token: session.session_token }).catch(() => {});
  };
  // Coupon attivo più vicino alla scadenza (valid_until; senza scadenza in coda)
  const featuredCoupon = (data.coupons || []).filter((cp) => cp.active_now)
    .sort((a, b) => (a.valid_until ? a.valid_until.slice(0, 10) : "9999").localeCompare(b.valid_until ? b.valid_until.slice(0, 10) : "9999"))[0] || null;
  const couponDisc = (cp) => (cp.discount_type === "percent" ? `-${cp.discount_value}%` : `-€ ${Number(cp.discount_value).toFixed(2)}`);
  const homeHref = `/home${localStorage.getItem("kl_tenant") ? `?t=${localStorage.getItem("kl_tenant")}&` : "?"}${c.branch_id ? `branch=${encodeURIComponent(c.branch_id)}` : ""}`;
  const enabledTiles = tilesOn(settings?.modules);
  const quickTiles = enabledTiles.filter((t) => QUICK_KEYS.includes(t.k));
  const areaTiles = enabledTiles.filter((t) => !QUICK_KEYS.includes(t.k));
  const personalMenu = [{ k: "card", l: "La mia card", icon: CreditCard }, ...enabledTiles].map((t) => ({ key: t.k, label: t.l, icon: t.icon, onClick: () => openTab(t.k) }));
  const publicTiles = applyTiles(MENU_SECTIONS.filter((m) => PUBLIC_TILE_KEYS.includes(m.key)));
  const bottomNav = [
    { key: "home", label: "Home", icon: HomeIcon, onClick: () => openTab("card"), active: tab === "card" },
    { key: "punti", label: "Punti", icon: History, onClick: () => openTab("history"), active: tab === "history" },
    { key: "premi", label: "Premi", icon: Gift, onClick: () => openTab("rewards"), active: tab === "rewards" },
    { key: "offerte", label: "Offerte", icon: Ticket, onClick: () => openTab("coupon"), active: tab === "coupon" },
    { key: "altro", label: "Altro", icon: MoreHorizontal, menu: true },
  ];
  const Pill = ({ children, onClick, href, testid, danger }) => {
    const cls = `inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-semibold shadow-sm ring-1 ring-zinc-200 hover:shadow transition-shadow ${danger ? "text-red-600" : ""}`;
    return href ? <a href={href} className={cls} style={danger ? undefined : { color: brand }} data-testid={testid}>{children}</a>
      : <button type="button" onClick={onClick} className={cls} style={danger ? undefined : { color: brand }} data-testid={testid}>{children}</button>;
  };
  const ActionBtn = ({ onClick, disabled, testid, children, extra }) => (
    <button data-testid={testid} disabled={disabled} onClick={onClick} {...extra}
      className="w-full rounded-2xl bg-white px-4 py-3 text-sm font-semibold shadow-sm ring-1 ring-zinc-200 flex items-center justify-center gap-2 hover:shadow disabled:opacity-50 transition-shadow" style={{ color: brand }}>
      {children}
    </button>
  );

  const avatarUrl = resolveImg(c.avatar_url);
  const initial = (c.name || "?").trim().charAt(0).toUpperCase();
  const Avatar = ({ size = "h-11 w-11", text = "text-base" }) => avatarUrl && !avatarBroken
    ? <img src={`${avatarUrl}${avatarUrl.includes("?") ? "&" : "?"}v=${encodeURIComponent(c.avatar_updated_at || "")}`} alt={c.name} onError={() => setAvatarBroken(true)} className={`${size} rounded-full object-cover ring-2 ring-white shadow`} data-testid="public-avatar-img" />
    : <span className={`${size} rounded-full flex items-center justify-center font-heading font-extrabold ${text} text-white ring-2 ring-white shadow`} style={{ background: "var(--client-brand-dark, #1E3A8A)" }} data-testid="public-avatar-initial">{initial}</span>;
  const headerRight = <>
    <button type="button" onClick={() => openTab("news")} aria-label="Notifiche" data-testid="public-bell" className="relative h-11 w-11 rounded-2xl bg-white shadow-sm ring-1 ring-zinc-200 flex items-center justify-center" style={{ color: brand }}>
      <Bell className="h-5 w-5" />
      {unread > 0 && <span data-testid="public-bell-count" className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow">{unread > 99 ? "99+" : unread}</span>}
    </button>
    <button type="button" onClick={() => openTab("card")} aria-label="La mia card" data-testid="public-header-avatar" className="rounded-full"><Avatar /></button>
  </>;

  return (
    <PublicShell testid="public-portal-page" title={tab !== "card" ? TAB_TITLES[tab] : undefined} onBack={() => openTab("card")} personal={personalMenu} onLogout={logout} bottomNav={bottomNav} headerRight={headerRight}>
      {tab === "card" && (
        <>
          {/* Card cliente: saluto, mini card, punti, codice QR (dialog) */}
          <div className="rounded-3xl p-5 text-white shadow-md space-y-4" style={{ background: `linear-gradient(135deg, ${brand} 0%, var(--client-brand-dark, ${brand}) 100%)` }} data-testid="public-card-panel">
            <div className="flex items-center gap-3">
              <span className="relative shrink-0">
              <button type="button" onClick={() => avatarInputRef.current?.click()} disabled={avatarBusy} aria-label="Cambia foto profilo" data-testid="public-avatar-button" className="relative block rounded-full disabled:opacity-60">
                <Avatar size="h-14 w-14" text="text-xl" />
                <span className="absolute -bottom-0.5 -right-0.5 h-6 w-6 rounded-full bg-white flex items-center justify-center shadow" style={{ color: brand }}>{avatarBusy ? <span className="h-3 w-3 rounded-full border-2 border-current border-t-transparent animate-spin" /> : <Camera className="h-3.5 w-3.5" />}</span>
              </button>
              {avatarUrl && <button type="button" onClick={removeAvatar} disabled={avatarBusy} aria-label="Rimuovi foto" data-testid="public-avatar-remove" className="absolute -top-1 -left-1 z-10 h-6 w-6 rounded-full bg-white text-red-600 flex items-center justify-center shadow hover:bg-red-50 disabled:opacity-60"><X className="h-3.5 w-3.5" /></button>}
              </span>
              <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" data-testid="public-avatar-input" onChange={(e) => uploadAvatar(e.target.files?.[0])} />
              <div className="min-w-0 flex-1">
                <div className="font-heading font-extrabold text-xl truncate" data-testid="public-greeting">Ciao {(c.name || "").split(" ")[0] || "cliente"}!</div>
                <div className="text-sm opacity-90 truncate">{c.card_status === "blocked" ? "La tua card è bloccata" : "La tua card è attiva"} · <span className="font-mono">{c.card_number}</span></div>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <span data-testid="public-card-type" className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-semibold">{c.card_type === "physical" ? <><CreditCard className="h-3 w-3" /> Fisica</> : <><Sparkles className="h-3 w-3" /> Virtuale</>}</span>
                {data.branch?.name && <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-semibold max-w-[130px] truncate" data-testid="public-branch-name"><MapPin className="h-3 w-3 shrink-0" /> {data.branch.name}</span>}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button type="button" onClick={() => setShowQr(true)} aria-label="Apri la card e il QR" className="relative w-36 h-24 rounded-xl overflow-hidden shadow-lg shrink-0 flex items-center justify-center text-left hover:scale-[1.02] active:scale-[0.98] transition-transform" style={{ background: "linear-gradient(135deg, rgba(255,255,255,.28), rgba(255,255,255,.08))" }} data-testid="public-mini-card">
                {data.branch_card_bg_url && <img src={data.branch_card_bg_url.startsWith("http") ? data.branch_card_bg_url : `${process.env.REACT_APP_BACKEND_URL}${data.branch_card_bg_url}`} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="absolute inset-0 w-full h-full object-cover opacity-70" data-testid="public-card-branch-bg" />}
                {resolveImg(data.brand?.logo_url) ? <img src={resolveImg(data.brand.logo_url)} alt="logo" className="relative h-12 max-w-[80%] object-contain drop-shadow" data-testid="public-brand-logo" /> : <span className="relative font-heading font-extrabold text-sm text-center px-2">{data.app_name || data.brand?.app_name}</span>}
                <span className="absolute bottom-1.5 left-2 text-[9px] font-mono opacity-80">{c.card_number}</span>
                <span className="absolute bottom-1 right-1.5 opacity-80"><QrCode className="h-3.5 w-3.5" /></span>
              </button>
              <div className="flex-1 min-w-0">
                <div className="text-sm opacity-90">Punti disponibili</div>
                <div className="flex items-center gap-2"><span className="font-heading font-extrabold text-4xl leading-none" data-testid="public-points">{c.points}</span><Star className="h-6 w-6 text-amber-300" fill="currentColor" /></div>
                {data?.group && <div className="text-[11px] mt-1 opacity-95" data-testid="public-group-balance">👨‍👩‍👧 {data.group.name}: {data.group.balance} pt condivisi ({data.group.members.length} carte)</div>}
              </div>
            </div>
            <CollectionBalances collections={data.collections || []} />
            <button type="button" onClick={() => setShowQr(true)} data-testid="public-show-qr" className="w-full rounded-2xl bg-white/15 ring-1 ring-white/30 px-4 py-3 flex items-center gap-3 font-semibold hover:bg-white/25 transition">
              <QrCode className="h-5 w-5" /> Il tuo codice cliente <ChevronRight className="h-4 w-4 ml-auto" />
            </button>
          </div>

          <InitiativesHome onCta={() => openTab("rewards")} />

          <section className="grid grid-cols-4 gap-2" data-testid="public-quick-actions">
            {quickTiles.map((t) => <SoftTile key={t.k} compact onClick={() => openTab(t.k)} icon={t.icon} label={t.l} colorKey={t.k} testid={`public-tile-${t.k}`}
              badge={t.k === "coupon" && activeCoupons > 0 ? <span data-testid="public-tile-coupon-badge" className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold flex items-center justify-center shadow">{activeCoupons}</span> : null} />)}
          </section>

          {featuredCoupon && (
            <button type="button" onClick={() => openCouponQr(featuredCoupon)} data-testid="public-featured-coupon"
              className="w-full text-left rounded-3xl bg-white p-2 shadow-sm ring-1 ring-zinc-200 hover:shadow-md transition-shadow active:scale-[0.99]">
              <div className="flex items-center gap-4 rounded-2xl px-4 py-3" style={{ background: `${brand}14` }}>
                <span className="h-14 w-14 shrink-0 rounded-2xl flex items-center justify-center shadow-md" style={{ background: brand, color: "#fff" }}><Ticket className="h-7 w-7" /></span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[11px] uppercase tracking-widest text-zinc-500">Coupon in evidenza{activeCoupons > 1 ? ` · ${activeCoupons} attivi` : ""}</span>
                  <span className="block font-heading font-extrabold text-xl whitespace-nowrap leading-tight" style={{ color: brand }} data-testid="public-featured-coupon-discount">{couponDisc(featuredCoupon)}</span>
                  <span className="block font-semibold text-sm text-zinc-800 truncate" data-testid="public-featured-coupon-name">{featuredCoupon.name}</span>
                  <span className="block text-xs text-zinc-600">
                    {featuredCoupon.valid_until ? <span className="text-amber-700 font-medium" data-testid="public-featured-coupon-expiry">Scade il {featuredCoupon.valid_until.slice(0, 10).split("-").reverse().join("/")}</span> : "Senza scadenza"}
                    {featuredCoupon.min_spend > 0 && <> · Min € {Number(featuredCoupon.min_spend).toFixed(2)}</>} · Tocca per il QR
                  </span>
                </span>
                <ChevronRight className="h-5 w-5 text-zinc-400 shrink-0" />
              </div>
            </button>
          )}

          <PromoBanner title="I tuoi premi ti aspettano!" cta="Scopri ora" onClick={() => openTab("rewards")} brand={brand} testid="public-rewards-banner" />

          <SectionTitle brand={brand} testid="public-areas-title">Le tue aree</SectionTitle>
          <section className="grid grid-cols-2 gap-3 !mt-3" data-testid="public-app-tiles">
            <CatalogHome />
            {areaTiles.map((t) => <AreaTile key={t.k} onClick={() => openTab(t.k)} icon={t.icon} label={t.l} colorKey={t.k} testid={`public-tile-${t.k}`} />)}
            {publicTiles.map((m) => <AreaTile key={m.key} to={m.to} icon={m.icon} label={m.label} colorKey={m.key} testid={`public-tile-${m.key}`} />)}
          </section>
          {data.daily_product && <span className="hidden" data-testid="public-tile-menu-daily-badge" />}

          <BannerCarousel banners={banners} brand={brand} appName={data.brand?.app_name} />

          <ProximityWatcher card={card} sessionToken={session.session_token} brand={brand} />

          <div className="client-light"><SponsorCarousel card={card} sessionToken={session.session_token} branchId={data.branch?.id} /></div>

          <div className="flex flex-wrap justify-center gap-2" data-testid="public-card-actions">
            <Pill href={homeHref} testid="public-home-link"><Newspaper className="h-3.5 w-3.5" /> News e Offerte</Pill>
            <Pill danger testid="public-lost-card" onClick={() => { if (window.confirm("Hai perso la carta? Verrà bloccata subito: nessuno potrà usarla. Per la sostituzione rivolgiti al punto vendita.")) lostCard(); }}>Ho perso la carta</Pill>
            <Pill testid="public-logout" onClick={logout}>Esci</Pill>
          </div>

          {typeof Notification !== "undefined" && Notification.permission !== "granted" && (
            <ActionBtn testid="public-enable-push" disabled={pushBusy} onClick={enablePush}><BellRing className="h-4 w-4" /> Attiva notifiche quando l'ordine è pronto</ActionBtn>
          )}
          {typeof Notification !== "undefined" && Notification.permission === "granted" && (
            <ActionBtn testid="public-test-push" disabled={pushBusy} onClick={testPush}><BellRing className="h-4 w-4" /> {pushBusy ? "Invio…" : "Prova la push"}</ActionBtn>
          )}
          {(walletStatus.apple || walletStatus.google) ? (
            <ActionBtn testid="public-add-to-wallet" disabled={walletBusy} onClick={addToWallet} extra={{ "data-variant": walletVariant }}>
              <Wallet className="h-4 w-4" /> {walletBusy ? "Preparazione…" : (walletVariant === "icon" ? "Wallet" : "Aggiungi al Wallet (Apple / Google)")}
            </ActionBtn>
          ) : (
            <ActionBtn testid="public-save-card" onClick={() => setSaveCardOpen(true)}>
              <Wallet className="h-4 w-4" /> Salva la card sul telefono
            </ActionBtn>
          )}
          <Dialog open={saveCardOpen} onOpenChange={setSaveCardOpen}>
            <DialogContent className="client-light max-w-sm" data-testid="public-save-card-dialog">
              <DialogHeader><DialogTitle className="flex items-center gap-2"><Wallet className="h-5 w-5" /> Salva la card sul telefono</DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm">
                <button type="button" onClick={saveCardImage} className="w-full rounded-2xl p-3 text-left text-white" style={{ background: "var(--client-brand, #1E40AF)" }} data-testid="public-save-card-image">
                  <div className="font-semibold flex items-center gap-2"><Download className="h-4 w-4" /> Scarica immagine della card</div>
                  <div className="text-xs opacity-90 mt-0.5">Con numero carta, punti e QR da mostrare in cassa: la trovi in Galleria/Foto.</div>
                </button>
                <div className="rounded-2xl border border-zinc-200 p-3" data-testid="public-save-card-home">
                  <div className="font-semibold flex items-center gap-2"><HomeIcon className="h-4 w-4" /> Aggiungi alla schermata Home</div>
                  <ul className="text-xs text-zinc-600 mt-1 space-y-1 list-disc pl-4">
                    <li><b>iPhone</b>: Safari → tasto Condividi <span className="font-mono">⎋</span> → "Aggiungi alla schermata Home".</li>
                    <li><b>Android</b>: Chrome → menu <span className="font-mono">⋮</span> → "Installa app" o "Aggiungi a schermata Home".</li>
                  </ul>
                  <div className="text-[11px] text-zinc-500 mt-1">La card si apre come un'app, con punti sempre aggiornati e notifiche.</div>
                </div>
                <p className="text-[11px] text-zinc-500">Apple Wallet e Google Wallet arrivano a breve: il pulsante comparirà qui.</p>
              </div>
            </DialogContent>
          </Dialog>

        </>
      )}

      <div className="client-light space-y-4">
        {tab === "coupon" && (
          (data.coupons || []).length === 0 ? (
            <div className="card-elev p-8 text-center text-zinc-500" data-testid="public-coupons-empty"><Ticket className="h-8 w-8 mx-auto mb-2 opacity-40" /><div className="text-sm">Nessun coupon disponibile al momento.</div></div>
          ) : (
            <div className="card-elev p-4" data-testid="public-coupons-panel">
              <div className="font-heading font-semibold text-sm mb-3 flex items-center gap-2">
                <Ticket className="h-4 w-4 text-amber-300" /> I tuoi coupon
              </div>
              <div className="space-y-2">
                {data.coupons.map((cp) => {
                  const disc = cp.discount_type === "percent" ? `-${cp.discount_value}%` : `-€ ${cp.discount_value.toFixed(2)}`;
                  const days = (cp.allowed_weekdays || []).map(d => ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"][d]).join(",");
                  const hours = (cp.allowed_hour_start != null && cp.allowed_hour_end != null)
                    ? `${String(cp.allowed_hour_start).padStart(2,"0")}–${String(cp.allowed_hour_end).padStart(2,"0")}`
                    : null;
                  // Live countdown: if coupon is active now, compute minutes until end of the current
                  // valid window (hour-end today or valid_until date). Refreshes every 60s via nowTick.
                  let countdown = null;
                  if (cp.active_now) {
                    const now = new Date(nowTick);
                    let endMs = null;
                    if (cp.allowed_hour_end != null && cp.allowed_hour_start != null) {
                      const hs = cp.allowed_hour_start, he = cp.allowed_hour_end;
                      const end = new Date(now);
                      if (hs < he) {
                        end.setHours(he, 0, 0, 0);
                      } else {
                        // window crosses midnight; end is tomorrow at he if we're past hs, else today at he
                        if (now.getHours() >= hs) { end.setDate(end.getDate() + 1); end.setHours(he, 0, 0, 0); }
                        else { end.setHours(he, 0, 0, 0); }
                      }
                      endMs = end.getTime();
                    } else if (cp.valid_until) {
                      // end of the valid_until date (23:59)
                      const d = new Date(cp.valid_until.slice(0, 10) + "T23:59:59");
                      endMs = d.getTime();
                    }
                    if (endMs) {
                      const diffMin = Math.max(0, Math.floor((endMs - now.getTime()) / 60000));
                      // Only show if within a "meaningful" window (< 48h)
                      if (diffMin < 60 * 48) {
                        const urgent = diffMin < 15;
                        if (diffMin < 60) countdown = { text: `Attivo ancora ${diffMin} min`, urgent };
                        else if (diffMin < 60 * 24) countdown = { text: `Attivo ancora ${Math.floor(diffMin / 60)}h ${diffMin % 60}min`, urgent };
                        else countdown = { text: `Attivo ancora ~${Math.floor(diffMin / 60 / 24)}g`, urgent: false };
                      }
                    }
                  }
                  return (
                    <button
                      type="button"
                      key={cp.id}
                      data-testid={`public-coupon-${cp.id}`}
                      onClick={() => openCouponQr(cp)}
                      className={`w-full text-left p-3 rounded-lg border transition-transform active:scale-[0.98] hover:brightness-110 ${cp.active_now ? "border-emerald-500/60 bg-emerald-500/5 hover:border-emerald-400" : "border-zinc-800 bg-zinc-950 opacity-70 hover:opacity-90"}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-amber-300 font-bold text-lg">{disc}</span>
                            <span className="pill pill-muted text-[10px] font-mono" data-testid={`public-coupon-code-${cp.id}`}>{cp.code}</span>
                            {cp.auto_kind && (
                              <span className={`pill text-[10px] ${cp.auto_kind === "birthday" ? "pill-red" : cp.auto_kind === "anniversary" ? "pill-mint" : "pill-gold"}`}
                                    data-testid={`public-coupon-autokind-${cp.id}`}>
                                {cp.auto_kind === "birthday" ? "🎂 Regalo compleanno" : cp.auto_kind === "anniversary" ? "🎁 Anniversario" : "💌 Ci manchi"}
                              </span>
                            )}
                            {cp.active_now && (
                              <span className="pill pill-mint text-[10px]" data-testid={`public-coupon-active-${cp.id}`}>
                                <Sparkles className="h-3 w-3" /> Attivo ora
                              </span>
                            )}
                            {countdown && (
                              <span
                                data-testid={`public-coupon-countdown-${cp.id}`}
                                className={`pill text-[10px] font-mono ${countdown.urgent ? "bg-red-500/20 text-red-300 border border-red-500/40 animate-pulse" : "bg-amber-500/15 text-amber-300 border border-amber-500/40"}`}
                              >
                                <Clock className="h-3 w-3" /> {countdown.text}
                              </span>
                            )}
                            {cp.wallet_only && (
                              <span className="pill pill-gold text-[10px]">Solo Wallet</span>
                            )}
                          </div>
                          <div className="text-xs text-zinc-300 mt-1 truncate">{cp.name}</div>
                          <div className="text-[11px] text-zinc-500 mt-1 flex flex-wrap gap-2">
                            {cp.min_spend > 0 && <span>Min € {cp.min_spend.toFixed(2)}</span>}
                            {days && <span>{days}</span>}
                            {hours && <span>{hours}</span>}
                            {cp.valid_until && <span>Fino {cp.valid_until.slice(0,10)}</span>}
                          </div>
                          {!cp.active_now && cp.inactive_reason && (
                            <div className="text-[11px] text-zinc-500 mt-1 italic" data-testid={`public-coupon-reason-${cp.id}`}>
                              {cp.inactive_reason}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )
        )}
        {tab === "flyer" && (
          <div className="space-y-3" data-testid="public-flyer-panel">
            <div className="card-elev p-4">
              <div className="font-heading font-semibold text-sm mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4 text-amber-300" /> Volantino & offerte
              </div>
              {flyers.length === 0 ? (
                <div className="text-xs text-zinc-500 text-center py-6">Nessuna promo attiva al momento.</div>
              ) : (
                <ul className="space-y-3">
                  {flyers.map((n) => {
                    const abs = (u) => !u ? null : (/^https?:\/\//.test(u) ? u : `${process.env.REACT_APP_BACKEND_URL}${u.startsWith("/") ? u : `/${u}`}`);
                    return (
                      <li key={n.id} data-testid={`public-flyer-item-${n.id}`} className="rounded-xl overflow-hidden border border-zinc-800 bg-zinc-950">
                        {n.cover_url && (
                          <img src={resolveImg(n.cover_url)} alt={n.title} className="w-full aspect-video object-cover" />
                        )}
                        <div className="p-3 space-y-1.5">
                          <h4 className="font-heading font-bold text-sm">{n.title}</h4>
                          {n.description && <p className="text-[12px] text-zinc-400 whitespace-pre-line">{n.description}</p>}
                          {(n.valid_from || n.valid_until) && (
                            <div className="text-[10px] text-zinc-500">
                              Valido {n.valid_from ? `dal ${n.valid_from.slice(0,10)}` : ""} {n.valid_until ? `al ${n.valid_until.slice(0,10)}` : ""}
                            </div>
                          )}
                          <div className="flex flex-wrap gap-2 pt-1">
                            {n.pdf_url && (
                              <>
                                <button
                                  onClick={() => setPdfViewer({ title: n.title, url: abs(n.pdf_url) })}
                                  data-testid={`public-flyer-preview-${n.id}`}
                                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[color:var(--paprika)] hover:brightness-110 text-white text-xs font-semibold"
                                >
                                  <FileText className="h-3.5 w-3.5" /> Anteprima PDF
                                </button>
                                <a
                                  href={abs(n.pdf_url)} download
                                  target="_blank" rel="noopener noreferrer"
                                  data-testid={`public-flyer-download-${n.id}`}
                                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-[color:var(--paprika)] text-[color:var(--paprika)] hover:bg-[color:var(--paprika)]/10 text-xs font-semibold"
                                >
                                  Scarica
                                </a>
                              </>
                            )}
                            {n.external_link && (
                              <a href={n.external_link} target="_blank" rel="noopener noreferrer"
                                 data-testid={`public-flyer-ext-${n.id}`}
                                 className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 text-xs font-semibold">
                                Scopri di più
                              </a>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        )}

        {tab === "menu" && (
          <div className="space-y-3" data-testid="public-menu-panel">
            {data.daily_product && (
              <DailyProductCard
                product={data.daily_product}
                onOrder={(p) => openMenuOrder(p)}
                onOpen={(p) => { const full = (data.products || []).find((x) => x.id === p.id); if (full) setProductDetail(full); }}
              />
            )}
            <div className="card-elev p-4">
              <div className="font-heading font-semibold text-sm mb-1 flex items-center gap-2">
                <Utensils className="h-4 w-4 text-amber-300" /> Prodotti disponibili
              </div>
              <p className="text-[11px] text-zinc-500 mb-3">
                {data.branch?.name
                  ? `Prodotti proposti dalla tua sede "${data.branch.name}".`
                  : "Prodotti proposti dalla rete Vivì."}
              </p>
              {(data.products || []).length === 0 ? (
                <div className="text-xs text-zinc-500 text-center py-8">
                  Nessun prodotto ancora pubblicato dalla tua sede.
                </div>
              ) : (() => {
                const allProducts = data.products || [];
                // Build category counts (using original list, not filtered)
                const catCounts = allProducts.reduce((acc, p) => {
                  const c = p.category || "Altro";
                  acc[c] = (acc[c] || 0) + 1;
                  return acc;
                }, {});
                const categories = Object.keys(catCounts).sort((a, b) => a.localeCompare(b, "it"));
                const q = menuSearch.trim().toLowerCase();
                const favCount = allProducts.filter((p) => favorites.has(p.id)).length;
                const filtered = allProducts.filter((p) => {
                  const cat = p.category || "Altro";
                  if (menuCat === "__FAV__") {
                    if (!favorites.has(p.id)) return false;
                  } else if (menuCat !== "__ALL__" && cat !== menuCat) {
                    return false;
                  }
                  if (!q) return true;
                  return (
                    (p.name || "").toLowerCase().includes(q) ||
                    (p.description || "").toLowerCase().includes(q) ||
                    cat.toLowerCase().includes(q)
                  );
                });
                const grouped = filtered.reduce((acc, p) => {
                  const c = p.category || "Altro";
                  (acc[c] = acc[c] || []).push(p);
                  return acc;
                }, {});
                const slug = (s) => (s || "altro").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "altro";
                return (
                  <>
                    {/* Search */}
                    <div className="relative mb-3">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500 pointer-events-none" />
                      <input
                        type="text"
                        value={menuSearch}
                        onChange={(e) => setMenuSearch(e.target.value)}
                        placeholder="Cerca prodotto…"
                        data-testid="public-menu-search"
                        className="w-full h-10 pl-9 pr-9 rounded-full bg-zinc-950 border border-zinc-800 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/60"
                      />
                      {menuSearch && (
                        <button
                          type="button"
                          onClick={() => setMenuSearch("")}
                          data-testid="public-menu-search-clear"
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-500 hover:text-amber-300"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    {/* Category chips */}
                    <div className="flex gap-2 overflow-x-auto pb-2 mb-3 -mx-1 px-1 no-scrollbar" data-testid="public-menu-cats">
                      <button
                        type="button"
                        onClick={() => setMenuCat("__ALL__")}
                        data-testid="public-menu-cat-all"
                        className={`shrink-0 px-3 h-8 rounded-full text-[11px] font-semibold uppercase tracking-wider transition-all border ${
                          menuCat === "__ALL__"
                            ? "bg-amber-400 text-zinc-950 border-amber-400 shadow-[0_0_0_3px_rgba(251,191,36,0.15)]"
                            : "bg-zinc-950 text-amber-300 border-zinc-800 hover:border-amber-500/60"
                        }`}
                      >
                        Tutte <span className={menuCat === "__ALL__" ? "text-zinc-700" : "text-zinc-600"}>· {allProducts.length}</span>
                      </button>
                      {favCount > 0 && (
                        <button
                          type="button"
                          onClick={() => setMenuCat("__FAV__")}
                          data-testid="public-menu-cat-favorites"
                          className={`shrink-0 px-3 h-8 rounded-full text-[11px] font-semibold uppercase tracking-wider transition-all border inline-flex items-center gap-1 ${
                            menuCat === "__FAV__"
                              ? "bg-rose-500 text-white border-rose-500 shadow-[0_0_0_3px_rgba(244,63,94,0.20)]"
                              : "bg-zinc-950 text-rose-300 border-zinc-800 hover:border-rose-500/60"
                          }`}
                        >
                          <Heart className={`h-3 w-3 ${menuCat === "__FAV__" ? "fill-white" : "fill-rose-400"}`} />
                          Preferiti <span className={menuCat === "__FAV__" ? "text-rose-100" : "text-zinc-600"}>· {favCount}</span>
                        </button>
                      )}
                      {categories.map((c) => {
                        const active = menuCat === c;
                        return (
                          <button
                            key={c}
                            type="button"
                            onClick={() => setMenuCat(c)}
                            data-testid={`public-menu-cat-${slug(c)}`}
                            className={`shrink-0 px-3 h-8 rounded-full text-[11px] font-semibold uppercase tracking-wider transition-all border ${
                              active
                                ? "bg-amber-400 text-zinc-950 border-amber-400 shadow-[0_0_0_3px_rgba(251,191,36,0.15)]"
                                : "bg-zinc-950 text-amber-300 border-zinc-800 hover:border-amber-500/60"
                            }`}
                          >
                            {c} <span className={active ? "text-zinc-700" : "text-zinc-600"}>· {catCounts[c]}</span>
                          </button>
                        );
                      })}
                    </div>
                    {filtered.length === 0 ? (
                      <div className="text-xs text-zinc-500 text-center py-8" data-testid="public-menu-empty">
                        {menuCat === "__FAV__" ? "Nessun preferito ancora — tocca il cuoricino su un prodotto." : "Nessun prodotto corrisponde ai filtri."}
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-3" data-testid="public-menu-grid">
                        {Object.entries(grouped).map(([cat, list]) => (
                          <React.Fragment key={cat}>
                            {menuCat === "__ALL__" && !q && (
                              <div className="col-span-2 text-[10px] uppercase tracking-widest text-amber-300 font-semibold pt-1">
                                {cat} <span className="text-zinc-600">· {list.length}</span>
                              </div>
                            )}
                            {list.map((p) => {
                              const isFav = favorites.has(p.id);
                              return (
                                <div
                                  key={p.id}
                                  className="relative rounded-xl overflow-hidden border border-zinc-800 bg-zinc-950 hover:border-amber-500/60 active:scale-[0.98] transition-all"
                                >
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); toggleFavorite(p.id); }}
                                    data-testid={`public-menu-fav-${p.id}`}
                                    aria-label={isFav ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}
                                    className="absolute top-1.5 right-1.5 z-10 h-8 w-8 rounded-full bg-black/55 backdrop-blur-sm border border-white/10 flex items-center justify-center hover:bg-black/70 active:scale-90 transition-all"
                                  >
                                    <Heart className={`h-4 w-4 transition-all ${isFav ? "fill-rose-500 text-rose-500" : "text-zinc-200"}`} />
                                  </button>
                                  <button
                                    type="button"
                                    data-testid={`public-menu-item-${p.id}`}
                                    onClick={() => setProductDetail(p)}
                                    title="Tocca per vedere i dettagli"
                                    className="text-left w-full block"
                                  >
                                    {p.image_url ? (
                                      <div className="aspect-square bg-zinc-900 overflow-hidden">
                                        <img src={resolveImg(p.image_url)} alt={p.name} loading="lazy" className="w-full h-full object-cover" />
                                      </div>
                                    ) : (
                                      <div className="aspect-square bg-gradient-to-br from-amber-500/10 to-zinc-900 flex items-center justify-center">
                                        <Utensils className="h-8 w-8 text-amber-300/40" />
                                      </div>
                                    )}
                                    <div className="p-2.5">
                                      <div className="font-heading text-sm text-zinc-100 leading-tight line-clamp-2 min-h-[2.5em]">
                                        {p.name}
                                      </div>
                                      {typeof p.price === "number" && p.price > 0 && (
                                        <div className="mt-1 text-amber-300 font-mono text-sm">€ {p.price.toFixed(2)}</div>
                                      )}
                                      {(p.allergens || []).length > 0 && (
                                        <div className="mt-1 text-[10px] text-amber-300/70 flex items-center gap-1">
                                          <span className="i">⚠</span>
                                          <span className="truncate">{(p.allergens || []).slice(0, 3).join(", ")}</span>
                                        </div>
                                      )}
                                    </div>
                                  </button>
                                </div>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
              {(data.products || []).length > 0 && (
                <button
                  onClick={() => openMenuOrder(null)}
                  data-testid="public-menu-order"
                  className="mt-4 w-full py-3 rounded-xl bg-[color:var(--paprika)] hover:brightness-110 text-white text-sm font-semibold flex items-center justify-center gap-2"
                >
                  <ShoppingBag className="h-4 w-4" /> Crea nuovo ordine
                </button>
              )}
            </div>
          </div>
        )}

        {tab === "branch" && (
          <div className="space-y-3" data-testid="public-branch-panel">
            <div className="card-elev p-4">
              <div className="font-heading font-semibold text-sm mb-3 flex items-center gap-2">
                <MapPin className="h-4 w-4 text-orange-400" /> La tua sede preferita
              </div>
              <p className="text-xs text-zinc-400 mb-3">
                Cambia in qualsiasi momento il negozio a cui sei associato: riceverai coupon, volantini e comunicazioni della sede scelta.
              </p>
              <div className="space-y-2">
                <button
                  onClick={() => changeBranch(null)}
                  disabled={branchBusy}
                  data-testid="public-branch-none"
                  className={`w-full text-left p-3 rounded-xl border transition-colors ${
                    !data?.customer?.branch_id
                      ? "border-amber-500 bg-amber-500/10 text-amber-100"
                      : "border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-zinc-700"
                  } ${branchBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-semibold">Nessuna preferenza</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">Ricevo comunicazioni di tutte le sedi</div>
                    </div>
                    {!data?.customer?.branch_id && <Check className="h-4 w-4 text-amber-400" />}
                  </div>
                </button>
                {branches.map((b) => {
                  const on = data?.customer?.branch_id === b.id;
                  return (
                    <button
                      key={b.id}
                      onClick={() => changeBranch(b.id)}
                      disabled={branchBusy || on}
                      data-testid={`public-branch-${b.id}`}
                      className={`w-full text-left p-3 rounded-xl border transition-colors ${
                        on ? "border-amber-500 bg-amber-500/10 text-amber-100"
                           : "border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-zinc-700"
                      } ${branchBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold truncate">{b.name}</div>
                          {b.address && <div className="text-[11px] text-zinc-500 mt-0.5 truncate">{b.address}</div>}
                          {b.phone && <div className="text-[11px] text-emerald-300 mt-0.5">{b.phone}</div>}
                        </div>
                        {on && <Check className="h-4 w-4 text-amber-400 shrink-0" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}


        {tab === "history" && (
          <div className="card-elev p-4" data-testid="public-history-panel">
            <div className="font-heading font-semibold text-sm mb-3 flex items-center gap-2">
              <History className="h-4 w-4 text-amber-300" /> Storico movimenti
            </div>
            {data.transactions.length === 0 && <div className="text-sm text-zinc-500 text-center py-6">Nessun movimento</div>}
            <ul className="space-y-2 max-h-[70vh] overflow-y-auto">
              {data.transactions.map((t) => (
                <li key={t.id} className={`p-3 rounded-lg bg-zinc-950 border border-zinc-800 flex items-center justify-between gap-3 ${t.cancelled ? "opacity-50 line-through" : ""}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">
                      {t.type === "earn" ? "Accumulo" : t.type === "redeem" ? `Riscatto · ${t.reward_name || ""}` : "Rettifica"}
                    </div>
                    <div className="text-[11px] text-zinc-500">
                      {new Date(t.created_at).toLocaleString("it-IT")}
                      {t.amount_eur ? ` · € ${t.amount_eur.toFixed(2)}` : ""}
                    </div>
                    {t.items && t.items.length > 0 && (
                      <div className="text-[10px] text-zinc-500 mt-1 truncate">
                        {t.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="text-right">
                      <span className={`font-mono font-bold text-sm ${t.points_delta > 0 ? "text-emerald-300" : "text-red-300"}`}>
                        {t.points_delta > 0 ? "+" : ""}{t.points_delta}
                      </span>
                      {t.collection_points && Object.values(t.collection_points).filter((v) => v.points).map((v, i) => <div key={i} className="text-[10px] font-mono text-sky-300" data-testid="public-tx-collection-points">+{v.points} {v.name}</div>)}
                      {t.collection_delta ? <div className="text-[10px] font-mono text-sky-300" data-testid="public-tx-collection-delta">{t.collection_delta > 0 ? "+" : ""}{t.collection_delta} {t.collection_name}</div> : null}
                    </div>
                    {t.type === "earn" && !t.cancelled && (
                      <button
                        data-testid={`public-tx-reprint-${t.id}`}
                        onClick={() => openReprint(t)}
                        className="p-1.5 rounded-md text-zinc-500 hover:text-sky-300 hover:bg-sky-500/10"
                        title="Ristampa scontrino"
                      >
                        <Printer className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {tab === "rewards" && (
          <div className="space-y-3" data-testid="public-rewards-panel">
            <GiftFromPoints card={card} sessionToken={session.session_token} points={data.customer?.points} appName={settings?.app_name} onDone={() => load()} />
            <CashbackCard card={card} sessionToken={session.session_token} refreshKey={data.customer?.cashback_balance} />
            <CustomerReview card={card} sessionToken={session.session_token} />
            <div className="card-elev p-4">
              <div className="font-heading font-semibold text-sm mb-3">Prenotazioni & ritiri</div>
              {data.reservations.length === 0 && <div className="text-xs text-zinc-500">Nessuna prenotazione</div>}
              <ul className="space-y-2">
                {data.reservations.map((r) => (
                  <li key={r.id} className="p-3 rounded-lg bg-zinc-950 border border-zinc-800 flex items-center justify-between gap-2" data-testid={`public-reservation-${r.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{r.reward_name}</div>
                      <div className="text-[11px] text-zinc-500">
                        {r.status === "pending" && "In lavorazione"}
                        {r.status === "ready" && "Pronto al ritiro"}
                        {r.status === "pickup_requested" && "Ritiro richiesto"}
                        {r.status === "delivered" && "Consegnato"}
                        {r.status === "cancelled" && "Annullato"}
                      </div>
                    </div>
                    {r.status === "ready" && (
                      <Button
                        data-testid={`public-pickup-${r.id}`}
                        size="sm"
                        onClick={() => requestPickup(r)}
                        className="bg-emerald-600 hover:bg-emerald-700 h-8"
                      >
                        <PkgIcon className="h-3 w-3 mr-1" /> Ritira
                      </Button>
                    )}
                    <PickupQr reservation={r} />
                  </li>
                ))}
              </ul>
            </div>

            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mt-4">Catalogo premi</div>
            <CollectionTabs collections={data.collections || []} value={collTab || (data.collections || []).find((x) => x.is_default)?.id} onChange={setCollTab} />
            <div className="grid grid-cols-1 gap-3">
              {(data.rewards || []).filter((r) => { const cols = data.collections || []; if (cols.length <= 1) return true; const sel = collTab || cols.find((x) => x.is_default)?.id; return (r.collection_id || cols.find((x) => x.is_default)?.id) === sel; }).map((r) => {
                const rColl = (data.collections || []).find((x) => x.id === (r.collection_id || (data.collections || []).find((y) => y.is_default)?.id));
                const bal = rColl ? rColl.balance : c.points;
                const enabled = bal >= r.points_cost && r.stock !== 0 && (!rColl || rColl.status === "active");
                const gallery = (r.images && r.images.length) ? r.images : (r.image_url ? [r.image_url] : []);
                const cover = gallery[0];
                return (
                  <div key={r.id} className={`card-elev p-4 flex items-center gap-3 relative ${enabled ? "" : "opacity-60"}`} data-testid={`public-reward-card-${r.id}`}>
                    <button
                      type="button"
                      onClick={() => cover && setGalleryReward(r)}
                      data-testid={`public-reward-image-${r.id}`}
                      className="shrink-0 relative"
                    >
                      {cover ? (
                        <>
                          <img src={resolveImg(cover)} alt={r.name} className="h-14 w-14 rounded-lg object-cover" />
                          {gallery.length > 1 && (
                            <span className="absolute -top-1 -right-1 pill pill-gold text-[9px] backdrop-blur-md bg-black/60 px-1.5 py-0.5" data-testid={`public-reward-gallery-badge-${r.id}`}>
                              +{gallery.length - 1}
                            </span>
                          )}
                        </>
                      ) : (
                        <div className="h-14 w-14 rounded-lg bg-amber-500/10 flex items-center justify-center"><Gift className="h-6 w-6 text-amber-300" /></div>
                      )}
                    </button>
                    <button
                      disabled={!enabled}
                      onClick={() => setReserving(r)}
                      data-testid={`public-reserve-${r.id}`}
                      className={`flex-1 flex items-center gap-2 text-left transition ${enabled ? "hover:text-amber-300" : "cursor-not-allowed"}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{r.name}</div>
                        <div className="text-[11px] text-zinc-500">
                          <span className="pill pill-gold font-mono">{r.points_cost} pt{r.redemption_mode === "both" ? <span className="text-[10px] font-sans"> oppure {r.points_cost_cash || r.points_cost} pt + € {Number(r.contribution_eur || 0).toFixed(2)}</span> : null}</span>
                          {r.contribution_eur > 0 && r.redemption_mode !== "both" && <> · <span className="text-red-300">+ € {r.contribution_eur.toFixed(2)}</span></>}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-zinc-500" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div className="space-y-3" data-testid="public-orders-panel">
            <Button
              data-testid="public-order-new"
              onClick={() => setOrderOpen(true)}
              className="w-full bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)] h-12"
            >
              <ShoppingBag className="h-4 w-4 mr-2" /> Invia nuova richiesta / ordine
            </Button>

            {paypalTest && <div className="rounded-xl bg-amber-100 text-amber-900 text-xs font-semibold px-3 py-2 text-center ring-1 ring-amber-300" data-testid="paypal-test-banner">MODALITÀ TEST PayPal: i pagamenti non sono reali</div>}
            <RecurringOrdersSection card={card} sessionToken={session.session_token} lastOrder={data.orders?.[0]} products={data.products || []} paypalOn={paypalOn} />
            {data.orders.length > 0 && (data.orders[0].items || []).length > 0 && (
              <Button
                data-testid="public-order-repeat"
                onClick={async () => {
                  const last = data.orders[0];
                  const items = (last.items || []).map(i => `${i.qty}× ${i.name}`).join(", ");
                  if (!window.confirm(`Ripetere l'ultimo ordine?\n${items}`)) return;
                  try {
                    await axios.post(`${API}/public/orders/${last.id}/repeat`, {
                      card_number: card, session_token: session.session_token,
                    });
                    toast.success("Ultimo ordine ripetuto ✓");
                    load();
                  } catch (err) { toast.error(formatErr(err)); }
                }}
                variant="outline"
                className="w-full border-amber-500/50 text-amber-300 hover:bg-amber-500/10 h-11"
              >
                🔄 Ripeti ultimo ordine ({(data.orders[0].items || []).length} articoli)
              </Button>
            )}
            <div className="card-elev p-4">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div className="font-heading font-semibold text-sm">Ordini inviati</div>
                {data.orders.length > 0 && (
                  <OrdersRangeSelector value={ordersRange} onChange={setOrdersRange} />
                )}
              </div>
              {data.orders.length > 0 && (
                <div className="relative mb-3">
                  <input
                    type="text"
                    value={ordersSearch}
                    onChange={(e) => setOrdersSearch(e.target.value)}
                    placeholder="🔍 Cerca per prodotto o nota…"
                    data-testid="public-orders-search"
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-xs text-white placeholder:text-zinc-600 focus:border-amber-500/60 outline-none"
                  />
                  {ordersSearch && (
                    <button
                      type="button"
                      onClick={() => setOrdersSearch("")}
                      data-testid="public-orders-search-clear"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white text-xs"
                    >
                      ✕
                    </button>
                  )}
                </div>
              )}
              {data.orders.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="public-orders-status-filter">
                  {[
                    { k: "all", label: "Tutti", cls: "bg-zinc-700 text-white border-zinc-600" },
                    { k: "received", label: "Ricevuti", cls: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
                    { k: "in_prep", label: "In preparazione", cls: "bg-blue-500/20 text-blue-300 border-blue-500/40" },
                    { k: "ready", label: "Pronti", cls: "bg-purple-500/20 text-purple-300 border-purple-500/40" },
                    { k: "delivered", label: "Consegnati", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
                    { k: "cancelled", label: "Annullati", cls: "bg-red-500/20 text-red-300 border-red-500/40" },
                  ].map((s) => {
                    const on = ordersStatusFilter === s.k;
                    return (
                      <button
                        key={s.k}
                        type="button"
                        onClick={() => setOrdersStatusFilter(s.k)}
                        data-testid={`public-orders-status-${s.k}`}
                        className={`text-[10px] px-2.5 py-1 rounded-full border transition ${on ? s.cls : "border-zinc-800 text-zinc-500 hover:border-zinc-600"}`}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => setOrdersReverse((v) => !v)}
                    data-testid="public-orders-reverse"
                    title={ordersReverse ? "Mostrati prima i più vecchi (clicca per invertire)" : "Mostrati prima i più recenti (clicca per invertire)"}
                    className={`ml-auto text-[10px] px-2.5 py-1 rounded-full border transition flex items-center gap-1 ${ordersReverse ? "bg-amber-500/20 text-amber-300 border-amber-500/40" : "border-zinc-800 text-zinc-500 hover:border-zinc-600"}`}
                  >
                    {ordersReverse ? "⬆️ Vecchi prima" : "⬇️ Recenti prima"}
                  </button>
                </div>
              )}
              {(() => {
                let filtered = filterOrdersByRange(data.orders, ordersRange);
                const q = ordersSearch.trim().toLowerCase();
                if (q) {
                  filtered = filtered.filter((o) => {
                    if ((o.note || "").toLowerCase().includes(q)) return true;
                    return (o.items || []).some((i) => (i.name || "").toLowerCase().includes(q));
                  });
                }
                if (ordersStatusFilter !== "all") {
                  filtered = filtered.filter((o) => (o.status || "") === ordersStatusFilter);
                }
                if (ordersReverse) filtered = [...filtered].reverse();
                if (data.orders.length === 0) {
                  return <div className="text-xs text-zinc-500 text-center py-4">Nessun ordine inviato ancora</div>;
                }
                if (filtered.length === 0) {
                  return <div className="text-xs text-zinc-500 text-center py-4" data-testid="public-orders-empty-range">
                    {q ? `Nessun ordine trovato per "${ordersSearch}"` : "Nessun ordine nel periodo selezionato"}
                  </div>;
                }
                return (
                  <>
                    <div className="text-[10px] text-zinc-500 mb-2" data-testid="public-orders-count">
                      {filtered.length} {filtered.length === 1 ? "ordine" : "ordini"}{q ? " trovato/i" : " nel periodo"}
                    </div>
                    <ul className="space-y-2">
                      {filtered.map((o) => (
                  <li key={o.id} className="p-3 rounded-lg bg-zinc-950 border border-zinc-800" data-testid={`public-order-${o.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-zinc-500">{new Date(o.created_at).toLocaleString("it-IT")}</div>
                      <span className={`pill text-[10px] ${o.status === "delivered" ? "pill-mint" : o.status === "received" ? "pill-gold" : "pill-muted"}`}>
                        {o.status === "received" && "✓ Ricevuto"}
                        {o.status === "in_prep" && "In preparazione"}
                        {o.status === "ready" && "Pronto"}
                        {o.status === "delivered" && "Consegnato"}
                        {o.status === "cancelled" && "Annullato"}
                      </span>
                    </div>
                    {o.note && <div className="text-sm mt-1">{o.note}</div>}
                    {o.items && o.items.length > 0 && (
                      <div className="text-[11px] text-zinc-500 mt-1">
                        {o.items.map((i, idx) => <div key={idx}>{i.qty}× {i.name}</div>)}
                      </div>
                    )}
                    {o.pickup_date && <div className="text-[11px] text-amber-300 mt-1">{o.fulfillment === "delivery" ? "Consegna" : "Ritiro"}: {o.pickup_date}</div>}
                    {o.fulfillment === "delivery" && o.delivery_address && <div className="text-[11px] text-sky-300 mt-0.5" data-testid={`public-order-delivery-${o.id}`}>🚚 A domicilio · {o.delivery_address}</div>}
                    {o.branch_name && <div className="text-[11px] text-zinc-500 mt-0.5">📍 {o.branch_name}</div>}
                    {o.payment?.status === "COMPLETED" ? (
                      <div className="text-[11px] text-emerald-600 mt-1 font-semibold" data-testid={`public-order-paid-${o.id}`}>✓ Pagato con PayPal · € {Number(o.payment.amount || 0).toFixed(2)}</div>
                    ) : paypalOn && !["delivered", "cancelled"].includes(o.status) && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button type="button" onClick={() => payOrderPayPal(o.id)} disabled={payingOrder === o.id} data-testid={`public-order-paypal-${o.id}`}
                          className="inline-flex items-center gap-2 rounded-full bg-[#0070BA] text-white text-xs font-semibold px-3 py-1.5 hover:bg-[#005ea6] disabled:opacity-60">
                          <Wallet className="h-3.5 w-3.5" /> {payingOrder === o.id ? "Reindirizzo a PayPal…" : `Paga ora con PayPal${o.total_amount ? ` · € ${Number(o.total_amount).toFixed(2)}` : ""}`}
                        </button>
                        {o.payment?.paypal_order_id && (
                          <button type="button" onClick={() => verifyOrderPayPal(o.id)} disabled={verifyingOrder === o.id} data-testid={`public-order-paypal-verify-${o.id}`}
                            className="inline-flex items-center gap-1.5 rounded-full border border-[#0070BA] text-[#0070BA] text-xs font-semibold px-3 py-1.5 hover:bg-[#0070BA]/10 disabled:opacity-60">
                            {verifyingOrder === o.id ? "Verifico…" : "Verifica pagamento"}
                          </button>
                        )}
                      </div>
                    )}
                    {o.ready_at && ["in_prep", "ready"].includes(o.status) && (
                      <div className="text-[11px] text-sky-300 mt-1" data-testid={`public-order-eta-${o.id}`}>
                        ⏱️ {o.status === "ready" ? "Pronto dalle" : o.rescheduled_count > 0 ? "Nuovo orario · pronto verso le" : "Pronto verso le"} {new Date(o.ready_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                        {o.status === "in_prep" && o.rescheduled_count > 0 && <span className="text-zinc-500"> · ci scusiamo per l'attesa 🙏</span>}
                      </div>
                    )}
                    {o.status === "received" && (() => {
                      const createdMs = new Date(o.created_at).getTime();
                      const elapsedMin = (Date.now() - createdMs) / 60000;
                      const remaining = Math.max(0, Math.floor(cancelWindow - elapsedMin));
                      if (elapsedMin > cancelWindow) return null;
                      return (
                        <button
                          type="button"
                          onClick={async () => {
                            if (!window.confirm(`Annullare l'ordine? Rimangono ~${remaining} min per farlo dall'app.`)) return;
                            try {
                              await axios.post(`${API}/public/orders/${o.id}/cancel`, {
                                card_number: card, session_token: session.session_token,
                              });
                              toast.success("Ordine annullato");
                              load();
                            } catch (err) { toast.error(formatErr(err)); }
                          }}
                          data-testid={`public-order-cancel-${o.id}`}
                          className="mt-2 w-full text-[11px] py-1.5 rounded-lg bg-red-500/15 text-red-300 border border-red-500/40 hover:bg-red-500/25 transition flex items-center justify-center gap-1"
                          title={`Puoi annullare per altri ~${remaining} min`}
                        >
                          <Trash2 className="h-3 w-3" /> Annulla ordine ({remaining} min rimanenti)
                        </button>
                      );
                    })()}
                    {o.status === "cancelled" && o.cancelled_by === "customer" && (
                      <div className="text-[10px] text-red-300 mt-1">🗑️ Annullato dal cliente</div>
                    )}
                  </li>
                      ))}
                    </ul>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {tab === "news" && (
          <NewsPanel card={card} sessionToken={session.session_token} />
        )}

        {tab === "chat" && (
          <ChatPanel card={card} sessionToken={session.session_token} />
        )}

        {tab === "referral" && (
          <ReferralPanel card={card} sessionToken={session.session_token} customerName={c.name} />
        )}

        {tab === "privacy" && (
          <ConsentsPanel card={card} sessionToken={session.session_token} />
        )}

        {tab === "guida" && <HowItWorksPanel brand={brand} />}

        {tab === "concorsi" && (
          <div className="space-y-3" data-testid="public-concorsi-panel">
            <div className="card-elev p-4">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="font-heading font-semibold text-sm flex items-center gap-2">
                  <Gamepad2 className="h-4 w-4 text-pink-400" /> {data.brand?.contest_title || "Concorsi"}
                </div>
                {data.brand?.contest_url && (
                  <a
                    href={data.brand.contest_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackContest("external")}
                    data-testid="public-concorsi-open-external"
                    className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                  >
                    <ExternalLink className="h-3 w-3" /> Apri
                  </a>
                )}
              </div>
              {data.brand?.contest_url ? (
                <>
                  <p className="text-[11px] text-zinc-500 mb-3 leading-relaxed">
                    Gioca al concorso attivo e prova a vincere premi extra oltre alla tua carta fedeltà.
                  </p>
                  <div className="rounded-xl overflow-hidden border border-zinc-800 bg-black" data-testid="public-concorsi-embed">
                    <iframe
                      src={data.brand.contest_url}
                      title={data.brand.contest_title || "Concorso"}
                      data-testid="public-concorsi-iframe"
                      width="100%"
                      height="820"
                      style={{ border: 0, display: "block" }}
                      allow="camera"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>
                  {contestBonus?.bonus_points > 0 && (
                    <p className="mt-3 text-xs rounded-lg px-3 py-2 bg-amber-50 text-amber-800 border border-amber-200" data-testid="public-concorsi-bonus-info">
                      {contestBonus.claimed_today ? `Bonus di oggi già ricevuto (+${contestBonus.bonus_points} pt). Torna domani!` : `Completa il concorso e premi il pulsante: ricevi +${contestBonus.bonus_points} punti (una volta al giorno).`}
                    </p>
                  )}
                  <button
                    onClick={completeContest}
                    disabled={!!contestBonus?.claimed_today}
                    data-testid="public-concorsi-complete"
                    className="mt-3 w-full text-xs bg-pink-500/10 border border-pink-500/40 text-pink-300 rounded-lg py-2 flex items-center justify-center gap-2 hover:bg-pink-500/20 disabled:opacity-50"
                  >
                    <Check className="h-3.5 w-3.5" /> Ho partecipato al concorso
                  </button>
                  <p className="text-[10px] text-zinc-600 text-center mt-2 leading-relaxed">
                    Aiutaci a capire quanti giocano davvero — segnala che hai completato il concorso.
                  </p>
                  {leaderboard && (leaderboard.top.length > 0 || leaderboard.prize) && (
                    <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/60 p-3" data-testid="public-concorsi-leaderboard">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="font-heading font-bold text-sm text-zinc-900 flex items-center gap-1.5"><Star className="h-4 w-4 text-amber-500" /> Classifica della settimana</div>
                        <span className="text-[10px] text-zinc-500">{leaderboard.week_start.slice(8)}/{leaderboard.week_start.slice(5, 7)} – {leaderboard.week_end.slice(8)}/{leaderboard.week_end.slice(5, 7)}</span>
                      </div>
                      {leaderboard.prize && <p className="text-xs text-amber-800 mb-2" data-testid="public-concorsi-prize">🏆 Premio al 1° classificato: <b>{leaderboard.prize}</b></p>}
                      {leaderboard.top.length === 0 ? <p className="text-xs text-zinc-500">Ancora nessuna partecipazione: sii il primo!</p> : (
                        <ol className="space-y-1">
                          {leaderboard.top.map((r) => (
                            <li key={r.rank} className={`flex items-center gap-2 text-xs rounded-lg px-2 py-1.5 ${r.me ? "bg-white ring-1 ring-amber-300 font-semibold" : ""}`} data-testid={`public-concorsi-rank-${r.rank}`}>
                              <span className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold ${r.rank === 1 ? "bg-amber-400 text-black" : "bg-zinc-200 text-zinc-700"}`}>{r.rank}</span>
                              <span className="flex-1 truncate text-zinc-800">{r.name}{r.me ? " (tu)" : ""}</span>
                              <span className="text-zinc-500">{r.plays} {r.plays === 1 ? "partecipazione" : "partecipazioni"}</span>
                            </li>
                          ))}
                        </ol>
                      )}
                      {leaderboard.me && leaderboard.me.rank > 10 && <p className="text-[11px] text-zinc-600 mt-2" data-testid="public-concorsi-myrank">La tua posizione: {leaderboard.me.rank}ª con {leaderboard.me.plays} partecipazioni</p>}
                      {contestHistory.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-amber-200" data-testid="public-concorsi-history">
                          <div className="text-[11px] font-semibold text-zinc-700 mb-1.5">Settimane passate</div>
                          <ul className="space-y-1">
                            {contestHistory.map((w) => (
                              <li key={w.week_start} className="flex items-center justify-between gap-2 text-[11px] text-zinc-600" data-testid={`public-concorsi-history-${w.week_start}`}>
                                <span>{w.week_start.slice(8)}/{w.week_start.slice(5, 7)} – {w.week_end.slice(8)}/{w.week_end.slice(5, 7)}</span>
                                <span className="font-semibold text-zinc-800">🏆 {w.winner ? `${w.winner.name} (${w.winner.plays})` : "nessun partecipante"}</span>
                                {w.prize && <span className="text-amber-700 truncate max-w-[40%]">{w.prize}</span>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-zinc-500" data-testid="public-concorsi-empty">
                  <Gamepad2 className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  <div className="text-xs">Nessun concorso attivo al momento.</div>
                  <div className="text-[10px] mt-1">Torna presto per nuove opportunità di vincere!</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <Dialog open={showQr} onOpenChange={setShowQr}>
        <DialogContent className="client-light bg-white border-zinc-200 max-w-sm text-center" data-testid="public-qr-dialog">
          <DialogHeader><DialogTitle className="font-heading text-center">Il tuo codice cliente</DialogTitle></DialogHeader>
          <img src={qrUrl} alt="QR" className="mx-auto rounded-xl bg-white p-2 ring-1 ring-zinc-200" width={220} height={220} />
          <div className="font-mono font-bold text-lg tracking-wider" data-testid="public-qr-card-number">{c.card_number}</div>
          <p className="text-xs text-zinc-500">Mostra il QR al negozio per accumulare o riscattare i punti.</p>
        </DialogContent>
      </Dialog>

      {/* Reserve reward dialog */}
      <Dialog open={!!reserving} onOpenChange={(o) => !o && setReserving(null)}>
        <DialogContent className="client-light bg-[color:var(--bg-surface)] border-zinc-800">
          <DialogHeader><DialogTitle>Prenota: {reserving?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            {reserving?.redemption_mode === "both" && (
              <div className="grid grid-cols-2 gap-2" data-testid="public-reserve-options">
                <button type="button" onClick={() => setReserveOption("points")} className={`rounded-lg border p-2 text-xs font-semibold ${reserveOption === "points" ? "border-amber-400 bg-amber-500/10" : "border-zinc-700"}`} data-testid="public-reserve-option-points">Solo punti<br /><span className="font-mono text-amber-300">{reserving.points_cost} pt</span></button>
                <button type="button" onClick={() => setReserveOption("cash")} className={`rounded-lg border p-2 text-xs font-semibold ${reserveOption === "cash" ? "border-amber-400 bg-amber-500/10" : "border-zinc-700"}`} data-testid="public-reserve-option-cash">Punti + contributo<br /><span className="font-mono text-amber-300">{reserving.points_cost_cash || reserving.points_cost} pt + € {Number(reserving.contribution_eur || 0).toFixed(2)}</span></button>
              </div>
            )}
            {(() => { const cash = reserving?.redemption_mode === "both" && reserveOption === "cash"; const cost = cash ? (reserving.points_cost_cash || reserving.points_cost) : (reserving?.points_cost || 0); return (<>
            <div className="font-mono">Costo: <span className="text-amber-300">{cost} pt</span>{cash && <span className="text-red-300"> + € {Number(reserving.contribution_eur || 0).toFixed(2)} contributo</span>}</div>
            <div className="text-xs text-zinc-500" data-testid="public-reserve-balance">{(() => { const rc = (data.collections || []).find((x) => x.id === (reserving?.collection_id || (data.collections || []).find((y) => y.is_default)?.id)); const b = rc ? rc.balance : c.points; return `${rc && !rc.is_default ? `Raccolta ${rc.name} · ` : ""}Saldo attuale: ${b} pt → dopo: ${b - cost} pt`; })()}</div>
            </>); })()}
            <Button data-testid="public-reserve-submit" onClick={reserve} className="w-full bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">
              Conferma prenotazione
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New order dialog */}
      <OrderDialog
        open={orderOpen}
        onClose={() => { setOrderOpen(false); setOrderInitialItem(null); }}
        card={card}
        sessionToken={session.session_token}
        paypalOn={paypalOn}
        onPayPal={payOrderPayPal}
        products={data.products || []}
        initialItem={orderInitialItem}
        branchId={c.branch_id || data.branch?.id || null}
        customerAddress={c.address || ""}
        onSuccess={load}
      />

      {/* Product detail (menu mini card) */}
      <ProductDetailDialog
        product={productDetail}
        allProducts={data.products || []}
        onClose={() => setProductDetail(null)}
        isFavorite={productDetail ? favorites.has(productDetail.id) : false}
        onToggleFavorite={toggleFavorite}
        onAddToOrder={(p, qty) => { setProductDetail(null); openMenuOrder(p, qty); }}
        onOpenProduct={(p) => setProductDetail(p)}
      />

      {/* Reward gallery lightbox */}
      <PublicGalleryLightbox item={galleryReward} onClose={() => setGalleryReward(null)} />

      {/* Full-screen PDF preview modal for flyers */}
      {pdfViewer && (
        <div className="fixed inset-0 z-50 bg-black/90 flex flex-col" data-testid="public-pdf-preview">
          <div className="flex items-center justify-between p-3 bg-black text-white">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="h-4 w-4 shrink-0" />
              <span className="truncate text-sm font-medium">{pdfViewer.title}</span>
            </div>
            <div className="flex items-center gap-2">
              <a href={pdfViewer.url} download target="_blank" rel="noopener noreferrer"
                 className="text-xs px-3 py-1.5 rounded-lg border border-white/30 hover:bg-white/10"
                 data-testid="public-pdf-preview-download">Scarica</a>
              <button
                onClick={() => setPdfViewer(null)}
                data-testid="public-pdf-preview-close"
                className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
                aria-label="Chiudi"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div className="flex-1 min-h-0 bg-zinc-100">
            <object data={`${pdfViewer.url}#toolbar=0&navpanes=0`} type="application/pdf" className="w-full h-full" data-testid="public-pdf-preview-object">
              <div className="p-6 text-center text-sm text-zinc-700">
                Il tuo dispositivo non può mostrare il PDF in anteprima.
                <a href={pdfViewer.url} download className="ml-2 underline text-red-600">Scarica il volantino</a>
              </div>
            </object>
          </div>
        </div>
      )}

      {/* Dialog QR coupon — cliente mostra al cassiere per la lettura */}
      <Dialog open={!!couponQr} onOpenChange={(o) => !o && setCouponQr(null)}>
        <DialogContent className="client-light bg-[color:var(--bg-surface)] border-zinc-800 max-w-sm" data-testid="public-coupon-qr-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ticket className="h-4 w-4 text-amber-300" /> Coupon da mostrare al negozio
            </DialogTitle>
          </DialogHeader>
          {couponQr && (
            <div className="space-y-3">
              <div className="text-center">
                <div className="font-heading font-bold text-2xl text-amber-300">
                  {couponQr.discount_type === "percent" ? `-${couponQr.discount_value}%` : `-€ ${couponQr.discount_value.toFixed(2)}`}
                </div>
                <div className="text-sm text-zinc-300 mt-1">{couponQr.name}</div>
              </div>
              <div className="mx-auto w-fit p-3 rounded-xl bg-white" data-testid="public-coupon-qr-image">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=260x260&margin=8&data=${encodeURIComponent(couponQr.code)}`}
                  alt={`QR ${couponQr.code}`}
                  width={220}
                  height={220}
                />
              </div>
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Codice coupon</div>
                <div className="font-mono text-lg font-bold text-white" data-testid="public-coupon-qr-code">{couponQr.code}</div>
              </div>
              <div className="text-[11px] text-zinc-500 text-center leading-relaxed">
                Mostra questo QR al cassiere per applicare lo sconto. Il codice può essere anche digitato manualmente.
              </div>
              {couponQr.valid_until && (
                <div className="text-center text-[11px] text-amber-300/80">
                  Valido fino al {couponQr.valid_until.slice(0, 10)}
                </div>
              )}
              {!couponQr.active_now && couponQr.inactive_reason && (
                <div className="text-center text-[11px] text-red-300 italic">
                  ⚠ {couponQr.inactive_reason}
                </div>
              )}

              {/* Storico utilizzi del coupon */}
              <div className="border-t border-zinc-800 pt-3 mt-1" data-testid="public-coupon-usage">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1">
                  <History className="h-3 w-3" /> Storico utilizzi
                </div>
                {(couponQr.uses_count || 0) === 0 ? (
                  <div className="text-[11px] text-zinc-400 italic" data-testid="public-coupon-usage-empty">
                    Non ancora utilizzato — puoi ancora usufruirne{couponQr.single_use ? " (uso singolo)" : ""}.
                  </div>
                ) : (
                  <div className="text-[11px] text-zinc-300 space-y-1" data-testid="public-coupon-usage-details">
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-500">Utilizzato</span>
                      <span className="font-mono text-emerald-300 font-bold">
                        {couponQr.uses_count}× {couponQr.single_use && couponQr.status === "used" ? "(esaurito)" : ""}
                      </span>
                    </div>
                    {couponQr.last_used_at && (
                      <div className="flex items-center justify-between">
                        <span className="text-zinc-500">Ultimo uso</span>
                        <span className="font-mono text-zinc-200">
                          {new Date(couponQr.last_used_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                    )}
                    {typeof couponQr.last_discount_eur === "number" && couponQr.last_discount_eur > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-zinc-500">Ultimo sconto</span>
                        <span className="font-mono text-amber-300">
                          − € {couponQr.last_discount_eur.toFixed(2)}
                          {typeof couponQr.last_amount_eur === "number" && couponQr.last_amount_eur > 0 ? ` (su € ${couponQr.last_amount_eur.toFixed(2)})` : ""}
                        </span>
                      </div>
                    )}
                    {couponQr.assigned_at && (
                      <div className="flex items-center justify-between text-zinc-500">
                        <span>Ricevuto il</span>
                        <span className="font-mono">
                          {new Date(couponQr.assigned_at).toLocaleDateString("it-IT")}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Sede associata su mappa OSM */}
              {(data?.branch?.address || data?.branch?.name) && (
                <div className="border-t border-zinc-800 pt-3" data-testid="public-coupon-branch-map">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1">
                    <MapPin className="h-3 w-3" /> Dove usarlo — {data.branch?.name || "Sede preferita"}
                  </div>
                  <AddressMap
                    customer={{ name: data.branch?.name, address: data.branch?.address }}
                    height={140}
                    compact={false}
                  />
                  {data.branch?.phone && (
                    <a
                      href={`tel:${data.branch.phone}`}
                      className="mt-2 inline-flex items-center gap-1 text-[11px] text-emerald-300 hover:text-emerald-200"
                      data-testid="public-coupon-branch-phone"
                    >
                      <Phone className="h-3 w-3" /> {data.branch.phone}
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Chatbot AI floating widget: parla con l'assistente per punti/coupon/premi */}
      <CustomerAiChat
        card={card}
        sessionToken={session?.session_token}
        primary={brand}
        bottomOffset={72}
        visible={!!(session && data)}
      />
    </PublicShell>
  );
}

function PublicGalleryLightbox({ item, onClose }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [item?.id]);
  if (!item) return null;
  const images = (item.images && item.images.length) ? item.images : (item.image_url ? [item.image_url] : []);
  if (!images.length) return null;
  const url = images[idx];
  const prev = (e) => { e?.stopPropagation(); setIdx((i) => (i - 1 + images.length) % images.length); };
  const next = (e) => { e?.stopPropagation(); setIdx((i) => (i + 1) % images.length); };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="client-light bg-[color:var(--bg-surface)] border-zinc-800 max-w-3xl p-0 overflow-hidden" data-testid="public-gallery-lightbox">
        <DialogHeader className="p-4 pb-2"><DialogTitle>{item.name}</DialogTitle></DialogHeader>
        <div className="relative bg-black">
          <img src={resolveImg(url)} alt={item.name} className="w-full max-h-[70vh] object-contain bg-black" data-testid={`public-gallery-img-${idx}`} />
          {images.length > 1 && (
            <>
              <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/70 hover:bg-black text-white flex items-center justify-center" data-testid="public-gallery-prev">‹</button>
              <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/70 hover:bg-black text-white flex items-center justify-center" data-testid="public-gallery-next">›</button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
                {images.map((_, i) => (
                  <span key={i} className={`h-1.5 w-1.5 rounded-full ${i === idx ? "bg-amber-300" : "bg-zinc-500/60"}`} />
                ))}
              </div>
            </>
          )}
        </div>
        {images.length > 1 && (
          <div className="p-3 flex gap-2 overflow-x-auto bg-zinc-950 border-t border-zinc-800">
            {images.map((u, i) => (
              <button key={i} onClick={() => setIdx(i)} className={`h-14 w-14 rounded-md overflow-hidden border ${i === idx ? "border-amber-400" : "border-zinc-800"} shrink-0`}>
                <img src={resolveImg(u)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function OrderDialog({ open, onClose, card, sessionToken, onSuccess, products = [], initialItem = null, branchId = null, customerAddress = "", paypalOn = false, onPayPal = null }) {
  const [note, setNote] = useState("");
  const [pickupDate, setPickupDate] = useState("");
  const [pickupTime, setPickupTime] = useState("");
  const [fulfillment, setFulfillment] = useState("pickup");
  const [payment, setPayment] = useState("store");
  const [giftCode, setGiftCode] = useState("");
  const [address, setAddress] = useState(customerAddress || "");
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState(branchId || "");
  const [catalog, setCatalog] = useState(products);
  const [catSearch, setCatSearch] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setItems(initialItem?.items?.length ? initialItem.items : initialItem?.name ? [{ name: initialItem.name, qty: initialItem.qty || 1, price: initialItem.price }] : []);
    setBranch(branchId || ""); setCatSearch(""); setFulfillment("pickup"); setPayment("store"); setAddress(customerAddress || "");
    axios.get(`${API}/public/branches`, tenantHeaders()).then(({ data }) => setBranches(data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // Catalogo del punto vendita selezionato (fallback: prodotti della sede del cliente)
  useEffect(() => {
    if (!open) return;
    if (!branch || branch === branchId) { setCatalog(products); return; }
    axios.get(`${API}/public/branch/${branch}/menu`, tenantHeaders()).then(({ data }) => setCatalog(data.products || [])).catch(() => setCatalog([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch, open]);

  const addProduct = (p) => {
    const i = items.findIndex((x) => x.name === p.name);
    if (i >= 0) { const c = [...items]; c[i] = { ...c[i], qty: (parseFloat(c[i].qty) || 1) + 1 }; setItems(c); }
    else setItems([...items, { name: p.name, qty: 1, price: p.price }]);
  };
  const setQty = (idx, q) => { const c = [...items]; c[idx] = { ...c[idx], qty: Math.max(1, q) }; setItems(c); };
  const removeRow = (idx) => setItems(items.filter((_, i) => i !== idx));
  const total = items.reduce((s, i) => s + (Number(i.price) || 0) * (parseFloat(i.qty) || 1), 0);
  const [cb, setCb] = useState(null);
  const [useCashback, setUseCashback] = useState(false);
  useEffect(() => { if (open) axios.post(`${API}/public/cashback`, { card_number: card, session_token: sessionToken }).then((r) => setCb(r.data)).catch(() => setCb(null)); }, [open, card, sessionToken]);
  const cbUse = useCashback && cb?.usable ? Math.min(cb.balance, total) : 0;
  const q = catSearch.trim().toLowerCase();
  const visible = catalog.filter((p) => !q || (p.name || "").toLowerCase().includes(q) || (p.category || "").toLowerCase().includes(q)).slice(0, q ? 30 : 12);
  const todayIso = new Date().toISOString().slice(0, 10);

  const submit = async () => {
    if (!items.length && !note.trim()) return toast.error("Aggiungi almeno un prodotto o una nota");
    if (fulfillment === "delivery" && !address.trim()) return toast.error("Indica l'indirizzo di consegna");
    const payNow = payment === "paypal" && paypalOn && onPayPal && total > 0;
    const payGift = payment === "giftcard";
    if (payGift && (!giftCode.trim() || total <= 0)) return toast.error("Inserisci il codice gift card e prodotti con prezzo");
    if (!window.confirm(payGift ? `Confermi l'ordine pagando € ${total.toFixed(2)} con la gift card ${giftCode.trim().toUpperCase()}?` : payNow ? `Confermi l'ordine e il pagamento di € ${total.toFixed(2)} con PayPal?` : fulfillment === "delivery" ? "Confermi l'ordine con consegna a domicilio?" : "Confermi l'invio della richiesta al negozio?")) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/orders`, {
        session_token: sessionToken, card_number: card,
        items: items.map((i) => ({ name: i.name.trim(), qty: parseFloat(i.qty) || 1, price: i.price ?? null })),
        note: note || null, pickup_date: pickupDate || null, pickup_time: pickupTime || null,
        branch_id: branch || null, fulfillment, delivery_address: fulfillment === "delivery" ? address.trim() : null,
        payment_method: payNow ? "paypal" : payGift ? "giftcard" : "store", gift_code: payGift ? giftCode.trim().toUpperCase() : null,
        cashback_use: !payNow && !payGift && cbUse > 0 ? Number(cbUse.toFixed(2)) : 0,
        source: initialItem?.source || null,
      });
      toast.success(payNow ? "Ordine inviato · ti porto su PayPal per il pagamento…" : `Ordine inviato alle ${new Date(data.received_at).toLocaleTimeString("it-IT")} ✓`);
      setNote(""); setPickupDate(""); setPickupTime(""); setItems([]);
      onClose(); onSuccess();
      if (payNow) await onPayPal(data.order?.id);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };

  const Seg = ({ v, label, icon: Icon, testid }) => (
    <button type="button" onClick={() => setFulfillment(v)} data-testid={testid}
      className={`flex-1 flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold border transition ${fulfillment === v ? "text-white border-transparent" : "bg-zinc-950 border-zinc-800 text-zinc-400"}`}
      style={fulfillment === v ? { background: "var(--client-brand, #1E40AF)" } : undefined}><Icon className="h-4 w-4" /> {label}</button>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="client-light bg-[color:var(--bg-surface)] border-zinc-800 max-h-[90vh] overflow-y-auto" data-testid="public-order-dialog">
        <DialogHeader><DialogTitle>Nuova richiesta / ordine</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-zinc-300 text-xs">Punto vendita</Label>
            <select value={branch} onChange={(e) => setBranch(e.target.value)} data-testid="public-order-branch"
              className="mt-1 w-full h-10 rounded-md border border-zinc-800 bg-zinc-950 px-3 text-sm">
              <option value="">{branchId ? "La mia sede" : "Scegli il punto vendita…"}</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}{b.address ? ` · ${b.address}` : ""}</option>)}
            </select>
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-300 text-xs">Catalogo prodotti {catalog.length > 0 && <span className="text-zinc-500">· {catalog.length} disponibili</span>}</Label>
            {catalog.length === 0 ? <div className="text-xs text-zinc-500" data-testid="public-order-catalog-empty">Nessun prodotto a catalogo per questa sede: descrivi la richiesta nelle note.</div> : (
              <>
                <Input value={catSearch} onChange={(e) => setCatSearch(e.target.value)} placeholder="Cerca nel catalogo…" data-testid="public-order-catalog-search" className="bg-zinc-950 border-zinc-800" />
                <div className="max-h-48 overflow-y-auto rounded-xl border border-zinc-800 divide-y divide-zinc-800" data-testid="public-order-catalog">
                  {visible.map((p) => (
                    <button key={p.id} type="button" onClick={() => addProduct(p)} data-testid={`public-order-product-${p.id}`} className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-zinc-900">
                      {p.image_url ? <img src={resolveImg(p.image_url)} alt="" className="h-9 w-9 rounded-lg object-cover shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <span className="h-9 w-9 rounded-lg bg-zinc-800 flex items-center justify-center shrink-0"><Utensils className="h-4 w-4 text-zinc-500" /></span>}
                      <span className="flex-1 min-w-0"><span className="block text-sm font-medium truncate">{p.name}</span>{p.category && <span className="block text-[10px] text-zinc-500 uppercase tracking-wider">{p.category}</span>}</span>
                      {typeof p.price === "number" && p.price > 0 && <span className="font-mono text-xs text-zinc-300">€ {p.price.toFixed(2)}</span>}
                      <span className="h-7 w-7 rounded-full flex items-center justify-center text-white text-lg leading-none" style={{ background: "var(--client-brand, #1E40AF)" }}>+</span>
                    </button>
                  ))}
                  {visible.length === 0 && <div className="px-3 py-3 text-xs text-zinc-500">Nessun prodotto corrisponde alla ricerca.</div>}
                </div>
              </>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-300 text-xs">Il tuo ordine {items.length > 0 && <span className="text-zinc-500">· {items.length} {items.length === 1 ? "articolo" : "articoli"}</span>}</Label>
            {items.length === 0 ? <div className="text-xs text-zinc-500" data-testid="public-order-items-empty">Tocca un prodotto del catalogo per aggiungerlo.</div> : items.map((it, idx) => (
              <div key={idx} className="flex items-center gap-2 rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2" data-testid={`public-order-item-${idx}`}>
                <span className="flex-1 min-w-0 text-sm font-medium truncate">{it.name}</span>
                {Number(it.price) > 0 && <span className="font-mono text-xs text-zinc-400">€ {(Number(it.price) * (parseFloat(it.qty) || 1)).toFixed(2)}</span>}
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => setQty(idx, (parseFloat(it.qty) || 1) - 1)} className="h-7 w-7 rounded-full border border-zinc-700 text-zinc-300" data-testid={`public-order-item-minus-${idx}`}>−</button>
                  <span className="w-6 text-center text-sm font-semibold" data-testid={`public-order-item-qty-${idx}`}>{it.qty}</span>
                  <button type="button" onClick={() => setQty(idx, (parseFloat(it.qty) || 1) + 1)} className="h-7 w-7 rounded-full border border-zinc-700 text-zinc-300" data-testid={`public-order-item-plus-${idx}`}>+</button>
                </div>
                <button type="button" onClick={() => removeRow(idx)} className="text-zinc-500 hover:text-red-400 pl-1" data-testid={`public-order-item-remove-${idx}`}><X className="h-4 w-4" /></button>
              </div>
            ))}
            {total > 0 && <div className="text-right text-sm font-semibold" data-testid="public-order-total">Totale indicativo: € {total.toFixed(2)}</div>}
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-300 text-xs">Come vuoi riceverlo?</Label>
            <div className="flex gap-2">
              <Seg v="pickup" label="Ritiro in negozio" icon={Store} testid="public-order-mode-pickup" />
              <Seg v="delivery" label="Consegna a casa" icon={Truck} testid="public-order-mode-delivery" />
            </div>
            {fulfillment === "delivery" && (
              <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Via, numero civico, città" data-testid="public-order-address" className="bg-zinc-950 border-zinc-800" />
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-zinc-300 text-xs">{fulfillment === "delivery" ? "Data consegna" : "Data ritiro"}</Label>
              <Input type="date" min={todayIso} value={pickupDate} onChange={(e) => setPickupDate(e.target.value)} data-testid="public-order-date" className="bg-zinc-950 border-zinc-800 mt-1" />
            </div>
            <div>
              <Label className="text-zinc-300 text-xs">Ora</Label>
              <Input type="time" step="900" value={pickupTime} onChange={(e) => setPickupTime(e.target.value)} data-testid="public-order-time" className="bg-zinc-950 border-zinc-800 mt-1" />
            </div>
          </div>

          <div>
            <Label className="text-zinc-300 text-xs">Note / richieste speciali</Label>
            <Textarea
              data-testid="public-order-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="bg-zinc-950 border-zinc-800"
              placeholder="Es. senza cipolla, citofono Rossi…"
            />
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">Metodo di pagamento</Label>
            <div className="mt-1 grid grid-cols-2 gap-2" data-testid="public-order-payment">
              <button type="button" onClick={() => setPayment("store")} data-testid="public-order-pay-store"
                className={`flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold border transition ${payment === "store" ? "text-white border-transparent" : "bg-zinc-950 border-zinc-800 text-zinc-400"}`}
                style={payment === "store" ? { background: "var(--client-brand, #1E40AF)" } : undefined}><Store className="h-4 w-4" /> {fulfillment === "delivery" ? "Alla consegna" : "In negozio"}</button>
              <button type="button" onClick={() => paypalOn && setPayment("paypal")} disabled={!paypalOn} data-testid="public-order-pay-paypal"
                className={`flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold border transition disabled:opacity-50 ${payment === "paypal" ? "bg-[#0070BA] text-white border-transparent" : "bg-zinc-950 border-zinc-800 text-zinc-400"}`}><Wallet className="h-4 w-4" /> PayPal</button>
            </div>
            {!paypalOn && <p className="text-[11px] text-zinc-500 mt-1">PayPal non attivo per questo negozio.</p>}
            <button type="button" onClick={() => setPayment("giftcard")} data-testid="public-order-pay-giftcard"
              className={`mt-2 w-full flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold border transition ${payment === "giftcard" ? "bg-emerald-600 text-white border-transparent" : "bg-zinc-950 border-zinc-800 text-zinc-400"}`}><Gift className="h-4 w-4" /> Paga con Gift Card</button>
            {payment === "giftcard" && <input value={giftCode} onChange={(e) => setGiftCode(e.target.value.toUpperCase())} placeholder="Codice gift card GC-XXXX-XXXX" className="mt-2 w-full rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm font-mono text-zinc-100" data-testid="public-order-gift-code" />}
            {payment === "paypal" && paypalOn && total <= 0 && <p className="text-[11px] text-amber-500 mt-1" data-testid="public-order-paypal-nototal">Aggiungi prodotti con prezzo per pagare con PayPal.</p>}
            {payment === "paypal" && paypalOn && total > 0 && <p className="text-[11px] text-zinc-500 mt-1">Dopo l'invio verrai portato su PayPal per pagare € {total.toFixed(2)}.</p>}
            {cb?.enabled && cb.use_online && cb.usable && total > 0 && payment === "store" && (
              <label className="mt-2 flex items-center justify-between gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm" data-testid="public-order-cashback-toggle">
                <span className="flex items-center gap-2 text-emerald-200"><Coins className="h-4 w-4" /> Usa il credito cashback (saldo € {cb.balance.toFixed(2)})</span>
                <input type="checkbox" checked={useCashback} onChange={(e) => setUseCashback(e.target.checked)} data-testid="public-order-cashback-checkbox" />
              </label>
            )}
            {useCashback && cbUse > 0 && <p className="text-[11px] text-emerald-300 mt-1" data-testid="public-order-cashback-summary">Credito applicato: −€ {cbUse.toFixed(2)} → da pagare € {(total - cbUse).toFixed(2)}</p>}
          </div>
          <Button
            data-testid="public-order-submit"
            onClick={submit}
            disabled={busy}
            className={`w-full ${payment === "paypal" && paypalOn ? "bg-[#0070BA] hover:bg-[#005ea6]" : "bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]"}`}
          >
            <Send className="h-4 w-4 mr-2" /> {busy ? "Invio…" : payment === "paypal" && paypalOn && total > 0 ? `Invia e paga con PayPal · € ${total.toFixed(2)}` : "Invia richiesta al negozio"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProductDetailDialog({ product, allProducts = [], onClose, isFavorite, onToggleFavorite, onAddToOrder, onOpenProduct }) {
  const [qty, setQty] = useState(1);
  const open = !!product;
  useEffect(() => { if (product) setQty(1); }, [product?.id]);
  if (!product) return (
    <Dialog open={false} onOpenChange={() => onClose()}>
      <DialogContent className="client-light hidden" />
    </Dialog>
  );
  const p = product;
  const price = typeof p.price === "number" && p.price > 0 ? p.price : null;
  const allergens = Array.isArray(p.allergens) ? p.allergens : [];
  const cat = (p.category || "").trim();
  // Recommended: same category (excluding current), else fallback to other random products
  const sameCat = allProducts.filter((x) => x.id !== p.id && ((x.category || "").trim() === cat) && cat);
  const others = allProducts.filter((x) => x.id !== p.id && ((x.category || "").trim() !== cat));
  const pool = [...sameCat];
  if (pool.length < 3) pool.push(...others);
  // Shuffle + take 3
  const recommended = pool
    .map((x) => [Math.random(), x])
    .sort((a, b) => a[0] - b[0])
    .map(([_, x]) => x)
    .slice(0, 3);
  const totalPrice = price !== null ? (price * qty) : null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="client-light bg-[color:var(--bg-surface)] border-zinc-800 max-w-md p-0 overflow-hidden max-h-[92vh] overflow-y-auto" data-testid="public-menu-detail-dialog">
        <DialogHeader className="sr-only">
          <DialogTitle>{p.name}</DialogTitle>
        </DialogHeader>
        <div className="relative">
          {p.image_url ? (
            <div className="aspect-[4/3] bg-zinc-900 overflow-hidden">
              <img src={resolveImg(p.image_url)} alt={p.name} className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="aspect-[4/3] bg-gradient-to-br from-amber-500/15 to-zinc-900 flex items-center justify-center">
              <Utensils className="h-16 w-16 text-amber-300/40" />
            </div>
          )}
          <button
            type="button"
            onClick={() => onToggleFavorite(p.id)}
            data-testid="public-menu-detail-fav"
            aria-label={isFavorite ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}
            className="absolute top-3 right-3 h-10 w-10 rounded-full bg-black/60 backdrop-blur-md border border-white/10 flex items-center justify-center hover:bg-black/80 active:scale-90 transition-all"
          >
            <Heart className={`h-5 w-5 transition-all ${isFavorite ? "fill-rose-500 text-rose-500" : "text-zinc-100"}`} />
          </button>
          {p.category && (
            <div className="absolute top-3 left-3">
              <span className="pill pill-gold text-[10px] backdrop-blur-md bg-black/50" data-testid="public-menu-detail-category">{p.category}</span>
            </div>
          )}
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="font-heading text-xl font-bold text-zinc-100 leading-tight" data-testid="public-menu-detail-name">{p.name}</div>
            {price !== null && (
              <div className="mt-1 text-amber-300 font-mono text-lg" data-testid="public-menu-detail-price">€ {price.toFixed(2)}</div>
            )}
          </div>
          {p.description && (
            <p className="text-sm text-zinc-400 leading-relaxed whitespace-pre-line" data-testid="public-menu-detail-description">{p.description}</p>
          )}
          {allergens.length > 0 && (
            <div className="rounded-lg border border-amber-500/25 bg-amber-500/5 p-3" data-testid="public-menu-detail-allergens">
              <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold flex items-center gap-1 mb-1.5">
                <AlertTriangle className="h-3 w-3" /> Allergeni
              </div>
              <div className="flex flex-wrap gap-1.5">
                {allergens.map((a, i) => (
                  <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-200 border border-amber-500/30">{a}</span>
                ))}
              </div>
            </div>
          )}
          {/* Quantity selector */}
          <div className="flex items-center justify-between rounded-xl border border-zinc-800 bg-zinc-950 p-3" data-testid="public-menu-detail-qty-row">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Quantità</div>
              {totalPrice !== null && (
                <div className="text-[11px] text-zinc-400 mt-0.5">Totale: <span className="font-mono text-amber-300">€ {totalPrice.toFixed(2)}</span></div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                disabled={qty <= 1}
                data-testid="public-menu-detail-qty-dec"
                className="h-9 w-9 rounded-full bg-zinc-900 border border-zinc-800 text-amber-300 disabled:opacity-40 hover:border-amber-500/60 active:scale-90 transition-all flex items-center justify-center font-bold"
              >−</button>
              <div className="w-10 text-center font-mono font-bold text-zinc-100" data-testid="public-menu-detail-qty-value">{qty}</div>
              <button
                type="button"
                onClick={() => setQty((q) => Math.min(99, q + 1))}
                disabled={qty >= 99}
                data-testid="public-menu-detail-qty-inc"
                className="h-9 w-9 rounded-full bg-zinc-900 border border-zinc-800 text-amber-300 disabled:opacity-40 hover:border-amber-500/60 active:scale-90 transition-all flex items-center justify-center font-bold"
              >+</button>
            </div>
          </div>
          <Button
            data-testid="public-menu-detail-add"
            onClick={() => onAddToOrder(p, qty)}
            className="w-full bg-[color:var(--paprika)] hover:brightness-110 text-white font-semibold"
          >
            <ShoppingBag className="h-4 w-4 mr-2" /> Aggiungi all'ordine · {qty}×
          </Button>
          {recommended.length > 0 && (
            <div className="pt-2" data-testid="public-menu-detail-recommended">
              <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold mb-2">
                Altri piatti da provare
              </div>
              <div className="grid grid-cols-3 gap-2">
                {recommended.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => onOpenProduct(r)}
                    data-testid={`public-menu-detail-reco-${r.id}`}
                    className="text-left rounded-lg overflow-hidden border border-zinc-800 bg-zinc-950 hover:border-amber-500/60 active:scale-[0.98] transition-all"
                  >
                    {r.image_url ? (
                      <div className="aspect-square overflow-hidden">
                        <img src={resolveImg(r.image_url)} alt={r.name} loading="lazy" className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="aspect-square bg-gradient-to-br from-amber-500/10 to-zinc-950 flex items-center justify-center">
                        <Utensils className="h-6 w-6 text-amber-300/40" />
                      </div>
                    )}
                    <div className="p-1.5">
                      <div className="font-heading text-[11px] text-zinc-100 leading-tight line-clamp-2 min-h-[2em]">{r.name}</div>
                      {typeof r.price === "number" && r.price > 0 && (
                        <div className="text-amber-300 font-mono text-[10px]">€ {r.price.toFixed(2)}</div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


function NewsPanel({ card, sessionToken }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all"); // "all" | "saved"
  const [heartPulse, setHeartPulse] = useState({}); // id -> boolean
  const lastTapRef = useRef({}); // id -> timestamp

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/public/notifications`, {
        params: { card_number: card, session_token: sessionToken },
      });
      setRows(data.notifications);
    } catch (err) { toast.error(formatErr(err)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/public/notifications/${id}/read`, {
        card_number: card, session_token: sessionToken,
      });
      setRows((r) => r.map((n) => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
    } catch (err) { /* silent */ }
  };

  const toggleLike = async (n) => {
    // Optimistic update
    const wasLiked = !!n.liked_at;
    setRows((r) => r.map((x) => x.id === n.id
      ? { ...x, liked_at: wasLiked ? null : new Date().toISOString(), read_at: x.read_at || new Date().toISOString() }
      : x));
    if (!wasLiked) {
      setHeartPulse((p) => ({ ...p, [n.id]: true }));
      setTimeout(() => setHeartPulse((p) => ({ ...p, [n.id]: false })), 800);
    }
    try {
      await axios.post(`${API}/public/notifications/${n.id}/like`, {
        card_number: card, session_token: sessionToken,
      });
    } catch (err) {
      toast.error(formatErr(err));
      setRows((r) => r.map((x) => x.id === n.id ? { ...x, liked_at: n.liked_at } : x));
    }
  };

  const toggleSave = async (n) => {
    const wasSaved = !!n.saved_at;
    setRows((r) => r.map((x) => x.id === n.id ? { ...x, saved_at: wasSaved ? null : new Date().toISOString() } : x));
    try {
      await axios.post(`${API}/public/notifications/${n.id}/save`, {
        card_number: card, session_token: sessionToken,
      });
      toast.success(wasSaved ? "Rimosso dai preferiti" : "Salvato tra i preferiti ⭐");
    } catch (err) {
      toast.error(formatErr(err));
      setRows((r) => r.map((x) => x.id === n.id ? { ...x, saved_at: n.saved_at } : x));
    }
  };

  const handleImageTap = (n) => {
    const now = Date.now();
    const last = lastTapRef.current[n.id] || 0;
    lastTapRef.current[n.id] = now;
    if (now - last < 350) {
      // double-tap → like
      if (!n.liked_at) toggleLike(n);
      else {
        // pulse heart even if already liked (Instagram behavior)
        setHeartPulse((p) => ({ ...p, [n.id]: true }));
        setTimeout(() => setHeartPulse((p) => ({ ...p, [n.id]: false })), 800);
      }
    } else if (!n.read_at) {
      markRead(n.id);
    }
  };

  const visible = filter === "saved" ? rows.filter((n) => n.saved_at) : rows;

  return (
    <div className="space-y-3" data-testid="public-news-panel">
      <div className="flex gap-2">
        <button
          data-testid="public-news-filter-all"
          onClick={() => setFilter("all")}
          className={`flex-1 py-2 rounded-lg text-xs font-medium ${filter === "all" ? "bg-[color:var(--paprika)] text-white" : "bg-zinc-900 text-zinc-400"}`}
        >
          <Bell className="h-3.5 w-3.5 inline mr-1" /> Tutte
        </button>
        <button
          data-testid="public-news-filter-saved"
          onClick={() => setFilter("saved")}
          className={`flex-1 py-2 rounded-lg text-xs font-medium ${filter === "saved" ? "bg-amber-500 text-black" : "bg-zinc-900 text-zinc-400"}`}
        >
          <Bookmark className="h-3.5 w-3.5 inline mr-1" /> Preferiti ({rows.filter(n => n.saved_at).length})
        </button>
      </div>

      {visible.length === 0 && (
        <div className="card-elev p-8 text-center text-sm text-zinc-500">
          {filter === "saved" ? "Nessuna news salvata. Tocca il segnalibro per aggiungerla ai preferiti." : "Nessuna news"}
        </div>
      )}

      {visible.map((n) => {
        const imgUrl = resolveImg(n.image_url);
        const pulsing = heartPulse[n.id];
        return (
          <article
            key={n.id}
            data-testid={`public-news-${n.id}`}
            className="card-elev overflow-hidden"
          >
            {/* Header */}
            <div className="p-3 flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <div className="h-8 w-8 rounded-full bg-[color:var(--paprika)] flex items-center justify-center shrink-0">
                  <Flame className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{n.title}</div>
                  <div className="text-[10px] text-zinc-500">{new Date(n.created_at).toLocaleString("it-IT")}</div>
                </div>
              </div>
              {!n.read_at && <span className="pill pill-gold text-[10px]">NEW</span>}
            </div>

            {/* Image with double-tap */}
            {imgUrl && (
              <div
                className="relative bg-black select-none cursor-pointer"
                data-testid={`public-news-image-${n.id}`}
                onClick={() => handleImageTap(n)}
                onDoubleClick={(e) => { e.preventDefault(); if (!n.liked_at) toggleLike(n); else { setHeartPulse((p) => ({...p, [n.id]: true})); setTimeout(() => setHeartPulse((p) => ({...p, [n.id]: false})), 800); } }}
              >
                <img
                  src={imgUrl}
                  alt={n.title}
                  className="w-full max-h-[70vh] object-cover"
                  draggable={false}
                />
                {pulsing && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <Heart
                      className="h-24 w-24 text-white drop-shadow-lg"
                      fill="white"
                      style={{
                        animation: "heartPop 0.8s ease-out forwards",
                        filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.6))",
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Actions bar (IG-style) */}
            <div className="px-3 pt-3 pb-1 flex items-center gap-4">
              <button
                data-testid={`public-news-like-${n.id}`}
                onClick={() => toggleLike(n)}
                className="transition-transform active:scale-125"
                aria-label="like"
              >
                <Heart
                  className={`h-6 w-6 transition-colors ${n.liked_at ? "text-red-500" : "text-zinc-300 hover:text-red-400"}`}
                  fill={n.liked_at ? "currentColor" : "none"}
                />
              </button>
              <button
                data-testid={`public-news-save-${n.id}`}
                onClick={() => toggleSave(n)}
                className="ml-auto transition-transform active:scale-125"
                aria-label="save"
              >
                <Bookmark
                  className={`h-6 w-6 transition-colors ${n.saved_at ? "text-amber-400" : "text-zinc-300 hover:text-amber-300"}`}
                  fill={n.saved_at ? "currentColor" : "none"}
                />
              </button>
            </div>

            {/* Body */}
            <div className="px-3 pb-3">
              <div className="text-sm text-zinc-200 whitespace-pre-wrap">
                <span className="font-semibold">{n.title} </span>
                {n.body}
              </div>
              {n.liked_at && (
                <div className="text-[11px] text-red-400 mt-1 flex items-center gap-1">
                  <Heart className="h-3 w-3" fill="currentColor" /> Ti piace
                </div>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
}

/* ---------------- Recurring Orders ---------------- */
const WEEKDAYS = [
  { v: 0, l: "Lunedì" },
  { v: 1, l: "Martedì" },
  { v: 2, l: "Mercoledì" },
  { v: 3, l: "Giovedì" },
  { v: 4, l: "Venerdì" },
  { v: 5, l: "Sabato" },
  { v: 6, l: "Domenica" },
];

function RecurringOrdersSection({ card, sessionToken, lastOrder, products = [], paypalOn = false }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/public/recurring-orders`, {
        params: { card_number: card, session_token: sessionToken },
      });
      setRows(data);
    } catch (err) { /* silent */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggleActive = async (r) => {
    try {
      await axios.patch(`${API}/public/recurring-orders/${r.id}`, {
        card_number: card, session_token: sessionToken, active: !r.active,
      });
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Eliminare l'ordine ricorrente del ${WEEKDAYS[r.weekday]?.l}?`)) return;
    try {
      await axios.delete(`${API}/public/recurring-orders/${r.id}`, {
        params: { card_number: card, session_token: sessionToken },
      });
      toast.success("Ordine ricorrente eliminato");
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const openFromLast = () => {
    if (lastOrder?.items?.length) {
      setPrefill({ items: lastOrder.items, note: lastOrder.note });
    } else {
      setPrefill(null);
    }
    setOpen(true);
  };

  return (
    <div className="card-elev p-4 space-y-2" data-testid="public-recurring-panel">
      <div className="flex items-center justify-between">
        <div className="font-heading font-semibold text-sm flex items-center gap-2">
          <Repeat className="h-4 w-4 text-emerald-300" /> Ordini ricorrenti
        </div>
        <Button
          data-testid="public-recurring-new"
          size="sm"
          onClick={openFromLast}
          className="bg-emerald-500/20 border border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/30 h-8"
        >
          + Nuovo
        </Button>
      </div>
      {rows.length === 0 && (
        <div className="text-xs text-zinc-500 py-2">
          Programma un ordine fisso (es. "ogni mercoledì") che parte in automatico ogni settimana.
        </div>
      )}
      {rows.map((r) => (
        <div key={r.id} data-testid={`public-recurring-${r.id}`} className="p-3 rounded-lg bg-zinc-950 border border-zinc-800 space-y-1">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-emerald-300 flex items-center gap-2">
              <Calendar className="h-3.5 w-3.5" /> Ogni {WEEKDAYS[r.weekday]?.l}
              {r.time_hint && <span className="text-xs text-zinc-400">· {r.time_hint}</span>}
              {r.payment_method === "paypal" && <span className="inline-flex items-center gap-1 rounded-full bg-[#0070BA]/15 text-[#4ea3e6] px-2 py-0.5 text-[10px] font-semibold" data-testid={`public-recurring-paypal-${r.id}`}><Wallet className="h-3 w-3" /> PayPal</span>}
            </div>
            <span className={`pill text-[10px] ${r.active ? "pill-mint" : "pill-muted"}`}>{r.active ? "Attivo" : "In pausa"}</span>
          </div>
          <div className="text-[11px] text-zinc-400">
            {(r.items || []).map((it, i) => <div key={i}>{it.qty}× {it.name}</div>)}
          </div>
          {r.note && <div className="text-[11px] text-zinc-500 italic">{r.note}</div>}
          <div className="flex gap-2 pt-1">
            <button
              data-testid={`public-recurring-toggle-${r.id}`}
              onClick={() => toggleActive(r)}
              className="text-[11px] text-zinc-400 hover:text-white underline"
            >
              {r.active ? "Metti in pausa" : "Riattiva"}
            </button>
            <button
              data-testid={`public-recurring-delete-${r.id}`}
              onClick={() => remove(r)}
              className="text-[11px] text-red-400 hover:text-red-300 underline ml-auto flex items-center gap-1"
            >
              <Trash2 className="h-3 w-3" /> Elimina
            </button>
          </div>
        </div>
      ))}

      <RecurringOrderDialog
        open={open}
        onClose={() => setOpen(false)}
        card={card}
        sessionToken={sessionToken}
        prefill={prefill}
        products={products}
        paypalOn={paypalOn}
        onCreated={load}
      />
    </div>
  );
}

function RecurringOrderDialog({ open, onClose, card, sessionToken, prefill, onCreated, products = [], paypalOn = false }) {
  const [weekday, setWeekday] = useState(2); // default: Mercoledì
  const [timeHint, setTimeHint] = useState("19:30");
  const [items, setItems] = useState([{ name: "", qty: 1 }]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [payment, setPayment] = useState("store");

  useEffect(() => {
    if (open) {
      if (prefill?.items?.length) {
        setItems(prefill.items.map((i) => ({ name: i.name, qty: i.qty || 1, price: i.price, product_id: i.product_id })));
      } else {
        setItems(products.length ? [] : [{ name: "", qty: 1 }]);
      }
      setNote(prefill?.note || "");
      setSearch("");
      setPayment(prefill?.payment_method === "paypal" ? "paypal" : "store");
    }
  }, [open, prefill, products.length]);

  const q = search.trim().toLowerCase();
  const visible = products.filter((p) => !q || (p.name || "").toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q) || (p.category || "").toLowerCase().includes(q));
  const addProduct = (p) => setItems((prev) => {
    const i = prev.findIndex((x) => x.product_id === p.id || (!x.product_id && x.name.trim().toLowerCase() === p.name.toLowerCase()));
    if (i >= 0) { const c = [...prev]; c[i] = { ...c[i], qty: (parseFloat(c[i].qty) || 0) + 1, price: p.price, product_id: p.id }; return c; }
    return [...prev, { name: p.name, qty: 1, price: p.price, product_id: p.id, description: p.description }];
  });
  const changeQty = (idx, d) => setItems((prev) => { const c = [...prev]; const n = (parseFloat(c[idx].qty) || 0) + d; if (n <= 0) return prev.filter((_, i) => i !== idx); c[idx] = { ...c[idx], qty: n }; return c; });
  const total = items.reduce((a, i) => a + (typeof i.price === "number" ? i.price * (parseFloat(i.qty) || 0) : 0), 0);

  const submit = async () => {
    const clean = items.filter((i) => (i.name || "").trim());
    if (!clean.length) return toast.error("Aggiungi almeno un prodotto");
    setBusy(true);
    try {
      await axios.post(`${API}/public/recurring-orders`, {
        card_number: card,
        session_token: sessionToken,
        weekday,
        time_hint: timeHint || null,
        items: clean.map((i) => ({ name: i.name.trim(), qty: parseFloat(i.qty) || 1, ...(typeof i.price === "number" ? { price: i.price } : {}), ...(i.product_id ? { product_id: i.product_id } : {}) })),
        note: note || null,
        payment_method: payment === "paypal" && paypalOn ? "paypal" : "store",
      });
      toast.success(`Ordine ricorrente programmato: ogni ${WEEKDAYS[weekday].l} 🔁`);
      onClose();
      onCreated?.();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="client-light bg-[color:var(--bg-surface)] border-zinc-800 max-h-[90vh] overflow-y-auto" data-testid="public-recurring-dialog">
        <DialogHeader><DialogTitle>Programma ordine ricorrente</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-zinc-300 text-xs">Giorno della settimana</Label>
            <div className="grid grid-cols-7 gap-1 mt-1">
              {WEEKDAYS.map((d) => (
                <button
                  key={d.v}
                  data-testid={`public-recurring-day-${d.v}`}
                  onClick={() => setWeekday(d.v)}
                  className={`py-2 text-xs rounded-lg ${weekday === d.v ? "bg-[color:var(--paprika)] text-white" : "bg-zinc-900 text-zinc-400"}`}
                >
                  {d.l.slice(0, 3)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">Ora indicativa (opz.)</Label>
            <Input data-testid="public-recurring-time" value={timeHint} onChange={(e) => setTimeHint(e.target.value)} placeholder="19:30" className="bg-zinc-950 border-zinc-800" />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300 text-xs">Catalogo prodotti {products.length > 0 && <span className="text-zinc-500">· {products.length} disponibili</span>}</Label>
            {products.length === 0 ? <div className="text-xs text-zinc-500" data-testid="public-recurring-catalog-empty">Nessun prodotto a catalogo: scrivi il nome del prodotto nelle righe qui sotto.</div> : (
              <>
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca nel catalogo…" data-testid="public-recurring-catalog-search" className="bg-zinc-950 border-zinc-800" />
                <div className="max-h-56 overflow-y-auto rounded-xl border border-zinc-800 divide-y divide-zinc-800" data-testid="public-recurring-catalog">
                  {visible.map((p) => (
                    <button key={p.id} type="button" onClick={() => addProduct(p)} data-testid={`public-recurring-product-${p.id}`} className="w-full flex items-start gap-3 px-3 py-2 text-left hover:bg-zinc-900">
                      {p.image_url ? <img src={resolveImg(p.image_url)} alt="" className="h-10 w-10 rounded-lg object-cover shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <span className="h-10 w-10 rounded-lg bg-zinc-800 flex items-center justify-center shrink-0"><Utensils className="h-4 w-4 text-zinc-500" /></span>}
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium truncate">{p.name}</span>
                        {p.description && <span className="block text-[11px] text-zinc-500 line-clamp-2" data-testid={`public-recurring-product-desc-${p.id}`}>{p.description}</span>}
                        {p.category && <span className="block text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5">{p.category}</span>}
                      </span>
                      <span className="flex flex-col items-end gap-1 shrink-0">
                        {typeof p.price === "number" && p.price > 0 && <span className="font-mono text-xs text-zinc-300" data-testid={`public-recurring-product-price-${p.id}`}>€ {p.price.toFixed(2)}</span>}
                        <span className="h-7 w-7 rounded-full flex items-center justify-center text-white text-lg leading-none" style={{ background: "var(--client-brand, #1E40AF)" }}>+</span>
                      </span>
                    </button>
                  ))}
                  {visible.length === 0 && <div className="px-3 py-3 text-xs text-zinc-500">Nessun prodotto corrisponde alla ricerca.</div>}
                </div>
              </>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300 text-xs">Prodotti scelti {items.length > 0 && <span className="text-zinc-500">· {items.length} {items.length === 1 ? "articolo" : "articoli"}</span>}</Label>
            {items.length === 0 && <div className="text-xs text-zinc-500" data-testid="public-recurring-items-empty">Tocca un prodotto del catalogo per aggiungerlo.</div>}
            {items.map((it, idx) => (
              <div key={idx} className="flex items-center gap-2" data-testid={`public-recurring-item-${idx}`}>
                {it.product_id ? (
                  <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{it.name}</div>{typeof it.price === "number" && it.price > 0 && <div className="text-[11px] text-zinc-500 font-mono">€ {it.price.toFixed(2)} × {it.qty} = € {(it.price * (parseFloat(it.qty) || 0)).toFixed(2)}</div>}</div>
                ) : (
                  <Input placeholder="Nome prodotto" value={it.name} data-testid={`public-recurring-item-name-${idx}`}
                    onChange={(e) => { const c=[...items]; c[idx] = { ...c[idx], name: e.target.value }; setItems(c); }}
                    className="bg-zinc-950 border-zinc-800 flex-1" />
                )}
                <div className="flex items-center rounded-full border border-zinc-800 overflow-hidden shrink-0">
                  <button type="button" onClick={() => changeQty(idx, -1)} className="px-2.5 py-1 text-zinc-300 hover:bg-zinc-800" data-testid={`public-recurring-item-minus-${idx}`}>−</button>
                  <span className="px-2 text-sm font-mono min-w-[1.5rem] text-center" data-testid={`public-recurring-item-qty-${idx}`}>{it.qty}</span>
                  <button type="button" onClick={() => changeQty(idx, 1)} className="px-2.5 py-1 text-zinc-300 hover:bg-zinc-800" data-testid={`public-recurring-item-plus-${idx}`}>+</button>
                </div>
                <button onClick={() => setItems(items.filter((_, i) => i !== idx))} className="text-zinc-500 hover:text-red-400 px-1" data-testid={`public-recurring-item-remove-${idx}`}>
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
            <div className="flex items-center justify-between gap-2">
              <Button variant="outline" size="sm" onClick={() => setItems([...items, { name: "", qty: 1 }])} className="border-zinc-800" data-testid="public-recurring-add-row">+ Prodotto fuori catalogo</Button>
              {total > 0 && <div className="text-sm font-semibold font-mono" data-testid="public-recurring-total">Totale stimato € {total.toFixed(2)}</div>}
            </div>
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">Metodo di pagamento</Label>
            <div className="mt-1 grid grid-cols-2 gap-2" data-testid="public-recurring-payment">
              <button type="button" onClick={() => setPayment("store")} data-testid="public-recurring-pay-store"
                className={`flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold border transition ${payment === "store" ? "text-white border-transparent" : "bg-zinc-950 border-zinc-800 text-zinc-400"}`}
                style={payment === "store" ? { background: "var(--client-brand, #1E40AF)" } : undefined}><Store className="h-4 w-4" /> In negozio</button>
              <button type="button" onClick={() => paypalOn && setPayment("paypal")} disabled={!paypalOn} data-testid="public-recurring-pay-paypal"
                className={`flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold border transition disabled:opacity-50 ${payment === "paypal" ? "bg-[#0070BA] text-white border-transparent" : "bg-zinc-950 border-zinc-800 text-zinc-400"}`}><Wallet className="h-4 w-4" /> PayPal</button>
            </div>
            <p className="text-[11px] text-zinc-500 mt-1" data-testid="public-recurring-payment-hint">
              {!paypalOn ? "PayPal non attivo per questo negozio." : payment === "paypal" ? "Ogni settimana riceverai una notifica con il pulsante «Paga ora con PayPal» per l'ordine generato." : "Paghi direttamente al ritiro o alla consegna."}
            </p>
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">Note</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="bg-zinc-950 border-zinc-800" placeholder="Es. senza cipolla, ritiro alle 20:30…" />
          </div>
          <Button
            data-testid="public-recurring-submit"
            onClick={submit}
            disabled={busy}
            className="w-full bg-emerald-600 hover:bg-emerald-500"
          >
            <Repeat className="h-4 w-4 mr-2" /> {busy ? "Salvo…" : "Programma ricorrenza"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Sponsor banner carousel: si auto-scorre ogni 6s, traccia impression/click. */
function SponsorCarousel({ card, sessionToken, branchId }) {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [expanded, setExpanded] = useState(null); // sponsor dettaglio con offerte
  const [contactMsg, setContactMsg] = useState("");
  const [contactOffer, setContactOffer] = useState(null);
  const [contactBusy, setContactBusy] = useState(false);
  const tracked = useRef(new Set());

  useEffect(() => {
    axios.get(`${API}/public/sponsors`, { params: branchId ? { branch_id: branchId } : {} })
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  }, [branchId]);

  useEffect(() => {
    if (items.length < 2 || expanded) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 6000);
    return () => clearInterval(t);
  }, [items.length, expanded]);

  useEffect(() => {
    const s = items[idx];
    if (!s || tracked.current.has(s.id)) return;
    tracked.current.add(s.id);
    axios.post(`${API}/public/sponsors/track`, {
      sponsor_id: s.id, event: "view", card_number: card, session_token: sessionToken,
    }).catch(() => {});
  }, [idx, items, card, sessionToken]);

  const sendContact = async () => {
    if (!contactMsg.trim()) return toast.error("Scrivi un messaggio");
    setContactBusy(true);
    try {
      await axios.post(`${API}/public/sponsors/${expanded.id}/contact`, {
        sponsor_id: expanded.id, card_number: card, session_token: sessionToken,
        message: contactMsg, offer_title: contactOffer?.title || null,
      });
      toast.success("Richiesta inviata allo sponsor ✓");
      setContactMsg(""); setContactOffer(null); setExpanded(null);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore invio"); }
    finally { setContactBusy(false); }
  };

  if (!items.length) return null;
  const s = items[idx];

  return (
    <div className="px-4 pb-2" data-testid="public-sponsor-carousel">
      <div
        onClick={() => setExpanded(s)}
        role="button"
        tabIndex={0}
        data-testid={`public-sponsor-${s.id}`}
        className="relative overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950 cursor-pointer hover:border-zinc-600 transition-all"
      >
        {s.video_url ? (
          <VideoPlayer url={s.video_url} poster={resolveImageUrl(s.image_url)} className="w-full h-40 object-cover bg-black" testid={`public-sponsor-video-${s.id}`} />
        ) : s.image_url ? (
          <img src={resolveImageUrl(s.image_url)} alt={s.name} className="w-full h-24 object-cover" />
        ) : (
          <div className="h-24 flex items-center justify-center bg-gradient-to-r from-amber-500/10 to-zinc-900">
            <div className="text-center px-3">
              <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-1">📢 Sponsor</div>
              <div className="font-heading font-bold text-white">{s.name}</div>
              {s.description && <div className="text-[11px] text-zinc-400 truncate">{s.description}</div>}
            </div>
          </div>
        )}
        <div className="absolute top-1.5 left-2 pill text-[9px] bg-black/60 text-white border border-white/20">Sponsor</div>
        {(s.offers && s.offers.length > 0) && (
          <div className="absolute top-1.5 right-2 pill text-[9px] bg-emerald-500/80 text-white">
            🎁 {s.offers.length} offert{s.offers.length === 1 ? "a" : "e"}
          </div>
        )}
        {items.length > 1 && !expanded && (
          <div className="absolute bottom-1.5 right-2 flex gap-1">
            {items.map((_, i) => (
              <span key={i} className={`w-1.5 h-1.5 rounded-full transition-all ${i === idx ? "bg-white w-3" : "bg-white/40"}`} />
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!expanded} onOpenChange={(o) => !o && setExpanded(null)}>
        <DialogContent className="client-light bg-[color:var(--bg-surface)] border-zinc-800 max-w-md max-h-[85vh] overflow-y-auto" data-testid="public-sponsor-dialog">
          {expanded && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">📢 {expanded.name}</DialogTitle>
              </DialogHeader>
              {expanded.video_url
                ? <VideoPlayer url={expanded.video_url} poster={resolveImageUrl(expanded.image_url)} background={false} className="w-full aspect-video rounded-lg bg-black" testid="public-sponsor-dialog-video" />
                : expanded.image_url && <img src={resolveImageUrl(expanded.image_url)} alt={expanded.name} className="w-full h-32 object-cover rounded-lg" />}
              {expanded.link && (
                <a href={expanded.link} target="_blank" rel="noreferrer" data-testid="public-sponsor-link-top"
                  onClick={() => axios.post(`${API}/public/sponsors/track`, { sponsor_id: expanded.id, event: "click", card_number: card, session_token: sessionToken }).catch(() => {})}
                  className="block text-center text-sm font-semibold rounded-full py-2.5 text-white" style={{ background: "var(--client-brand, #1e3a8a)" }}>
                  Scopri di più ↗
                </a>
              )}
              {expanded.description && <p className="text-xs text-zinc-400" data-testid="public-sponsor-description">{expanded.description}</p>}

              {expanded.offers && expanded.offers.length > 0 && (
                <div className="space-y-2" data-testid="public-sponsor-offers">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-300 flex items-center gap-1">🎁 Offerte disponibili</div>
                  {expanded.offers.map((o, i) => (
                    <div key={i} data-testid={`public-sponsor-offer-${i}`} className="rounded-lg bg-emerald-500/5 border border-emerald-500/30 p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-heading font-bold text-sm text-emerald-200">{o.title}</div>
                          {o.description && <div className="text-[11px] text-zinc-400 mt-0.5">{o.description}</div>}
                          {o.code && <div className="font-mono text-xs text-amber-300 mt-1">Codice: <b>{o.code}</b></div>}
                        </div>
                        {expanded.contact_enabled && (
                          <button
                            onClick={() => setContactOffer(o)}
                            data-testid={`public-sponsor-offer-adhere-${i}`}
                            className="shrink-0 px-3 py-1.5 rounded-lg bg-[color:var(--client-brand)] hover:bg-[color:var(--client-brand-dark)] text-white text-[11px] font-bold"
                          >
                            Aderisci
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {expanded.contact_enabled && (
                <div className="border-t border-zinc-800 pt-3 space-y-2" data-testid="public-sponsor-contact">
                  <div className="text-[10px] uppercase tracking-widest text-blue-300">📨 Contatta lo sponsor</div>
                  {contactOffer && (
                    <div className="text-[11px] px-2 py-1 rounded bg-emerald-500/10 text-emerald-300 flex items-center justify-between">
                      <span>Adesione a: <b>{contactOffer.title}</b></span>
                      <button onClick={() => setContactOffer(null)} className="text-zinc-500 hover:text-red-400">✕</button>
                    </div>
                  )}
                  <textarea
                    data-testid="public-sponsor-contact-msg"
                    value={contactMsg}
                    onChange={(e) => setContactMsg(e.target.value)}
                    placeholder={contactOffer ? `Sono interessato all'offerta "${contactOffer.title}"...` : "Scrivi la tua richiesta..."}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-lg p-2 text-xs h-20 resize-none"
                  />
                  <button
                    onClick={sendContact}
                    disabled={contactBusy || !contactMsg.trim()}
                    data-testid="public-sponsor-contact-send"
                    className="w-full py-2.5 rounded-lg bg-[color:var(--client-brand)] hover:bg-[color:var(--client-brand-dark)] disabled:opacity-40 text-white font-bold text-sm flex items-center justify-center gap-2"
                  >
                    <Send className="h-4 w-4" /> {contactBusy ? "Invio…" : "Invia richiesta"}
                  </button>
                  {expanded.contact_phone && (
                    <a
                      href={`https://wa.me/${expanded.contact_phone.replace(/[^\d+]/g, "")}?text=${encodeURIComponent(contactOffer ? `Ciao! Sono interessato all'offerta "${contactOffer.title}"` : "Ciao! Ho visto il vostro sponsor sull'app Vivi Fidelity Club")}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="public-sponsor-contact-wa"
                      className="w-full inline-flex items-center justify-center gap-2 py-2 rounded-lg bg-[#25D366] hover:bg-[#20B858] text-white font-bold text-xs"
                    >
                      <MessageCircle className="h-4 w-4" /> Contatta su WhatsApp
                    </a>
                  )}
                </div>
              )}

              {expanded.link && (
                <a
                  href={expanded.link}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => axios.post(`${API}/public/sponsors/track`, { sponsor_id: expanded.id, event: "click", card_number: card, session_token: sessionToken }).catch(() => {})}
                  data-testid="public-sponsor-external-link"
                  className="w-full inline-flex items-center justify-center gap-1 text-[11px] text-blue-400 hover:text-blue-300 py-2 underline"
                >
                  Visita il sito dello sponsor ↗
                </a>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Referral panel: mostra codice invito, amici invitati, bonus e share WhatsApp. */
function ReferralPanel({ card, sessionToken, customerName }) {
  const { settings } = useBrand();
  const [ref, setRef] = useState(null);
  useEffect(() => {
    axios.get(`${API}/public/customer/${card}/referral`, { params: { session_token: sessionToken } })
      .then((r) => setRef(r.data)).catch(() => setRef(null));
  }, [card, sessionToken]);
  if (!ref) return <div className="card-elev p-5 text-center text-zinc-500 text-xs">Caricamento…</div>;
  const slug = localStorage.getItem("kl_tenant");
  const regLink = `${window.location.origin}${slug ? `/${slug}` : ""}/registrati?ref=${ref.referral_code}`;
  const shareText = `🎁 Ciao! Ti invito su ${settings?.app_name || "Vivi Fidelity Club"}. Usa il mio codice ${ref.referral_code} in fase di registrazione: riceverai subito 50 punti e li riceverò anch'io. Registrati qui: ${regLink}`;
  const waLink = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const copy = () => { navigator.clipboard.writeText(ref.referral_code); toast.success("Codice copiato ✓"); };
  const copyLink = () => { navigator.clipboard.writeText(regLink); toast.success("Link copiato ✓"); };
  return (
    <div className="space-y-3 animate-fade-up" data-testid="public-referral-panel">
      <div className="card-elev p-5 text-center bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/30">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-2">🎁 Invita un amico</div>
        <div className="text-xs text-zinc-400 mb-3">Ricevi <b className="text-emerald-300">+50 punti</b> per ogni amico che si iscrive col tuo codice. Anche lui riceve +50 punti!</div>
        <div className="rounded-xl bg-zinc-950 border border-emerald-500/30 p-4 mb-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Il tuo codice</div>
          <div className="font-mono font-bold text-3xl text-emerald-300 tracking-wider" data-testid="public-referral-code">{ref.referral_code}</div>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="rounded-lg bg-zinc-950 border border-zinc-800 p-3" data-testid="public-referral-invited">
            <div className="text-[10px] uppercase text-zinc-500 mb-1">Amici invitati</div>
            <div className="font-heading font-bold text-2xl text-emerald-300">{ref.invited_count}</div>
          </div>
          <div className="rounded-lg bg-zinc-950 border border-zinc-800 p-3" data-testid="public-referral-bonus">
            <div className="text-[10px] uppercase text-zinc-500 mb-1">Punti guadagnati</div>
            <div className="font-heading font-bold text-2xl text-amber-300">+{ref.total_bonus_earned}</div>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-2">
          <a
            href={waLink}
            target="_blank"
            rel="noreferrer"
            data-testid="public-referral-share-wa"
            className="inline-flex items-center justify-center gap-2 py-3 rounded-lg bg-[#25D366] hover:bg-[#20b858] text-white font-bold text-sm"
          >
            <MessageCircle className="h-4 w-4" /> Condividi su WhatsApp
          </a>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={copy}
              data-testid="public-referral-copy-code"
              className="py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-zinc-300 text-xs hover:bg-zinc-800"
            >
              Copia codice
            </button>
            <button
              onClick={copyLink}
              data-testid="public-referral-copy-link"
              className="py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-zinc-300 text-xs hover:bg-zinc-800"
            >
              Copia link invito
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Pannello Revoca Consensi (art. 7 GDPR): l'utente accende/spegne i 3 consensi facoltativi. */
function ConsentsPanel({ card, sessionToken }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => {
    axios.get(`${API}/public/customer/${card}/consents`, { params: { session_token: sessionToken } })
      .then((r) => setState(r.data)).catch(() => setState(null));
  }, [card, sessionToken]);

  const toggle = async (key, value) => {
    setBusy(key);
    try {
      const { data } = await axios.patch(`${API}/public/customer/${card}/consents`, {
        card_number: card, session_token: sessionToken, [key]: value,
      });
      setState(data);
      toast.success(value ? "Consenso attivato ✓" : "Consenso revocato ✓");
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setBusy(null); }
  };

  if (!state) return <div className="card-elev p-5 text-center text-zinc-500 text-xs">Caricamento…</div>;

  const items = [
    { key: "consent_profiling", title: "Profilazione", desc: "Analisi delle abitudini di acquisto per offerte personalizzate." },
    { key: "consent_marketing_email", title: "Marketing via email", desc: "Email con promozioni, coupon e novità." },
    { key: "consent_marketing_wa", title: "Comunicazioni WhatsApp / SMS", desc: "Messaggi promozionali su WhatsApp o via SMS." },
  ];

  return (
    <div className="space-y-3 animate-fade-up" data-testid="public-consents-panel">
      <div className="card-elev p-5">
        <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-2 flex items-center gap-2">
          <ShieldCheck className="h-3 w-3" /> I miei consensi Privacy
        </div>
        <p className="text-xs text-zinc-400 mb-4">
          Puoi attivare o revocare i consensi facoltativi in qualsiasi momento (art. 7 GDPR). Il consenso base (Trattamento dati) è necessario per la carta e non può essere revocato senza chiudere l'account.
        </p>

        <div className="rounded-lg bg-emerald-500/5 border border-emerald-500/30 p-3 mb-3 flex items-center justify-between" data-testid="consent-treatment-row">
          <div>
            <div className="text-xs font-semibold text-emerald-200">1. Trattamento dati*</div>
            <div className="text-[11px] text-zinc-400">Obbligatorio per la gestione della carta.</div>
          </div>
          <span className="pill pill-mint text-[9px]"><CheckCircle2 className="h-3 w-3" /> Attivo</span>
        </div>

        {items.map((it) => {
          const active = !!state[it.key];
          const isBusy = busy === it.key;
          return (
            <div key={it.key} data-testid={`consent-${it.key}-row`} className="rounded-lg bg-zinc-950 border border-zinc-800 p-3 mb-2 flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-zinc-200">{it.title}</div>
                <div className="text-[11px] text-zinc-500">{it.desc}</div>
              </div>
              <div className="flex gap-1.5 shrink-0">
                <button
                  disabled={isBusy}
                  onClick={() => toggle(it.key, true)}
                  data-testid={`consent-${it.key}-yes`}
                  className={`h-8 px-3 rounded-lg text-xs font-bold transition-all ${active ? "bg-emerald-500 text-white" : "bg-zinc-900 border border-zinc-700 text-zinc-400 hover:border-emerald-500/60"}`}
                >
                  SI
                </button>
                <button
                  disabled={isBusy}
                  onClick={() => toggle(it.key, false)}
                  data-testid={`consent-${it.key}-no`}
                  className={`h-8 px-3 rounded-lg text-xs font-bold transition-all ${!active ? "bg-red-500 text-white" : "bg-zinc-900 border border-zinc-700 text-zinc-400 hover:border-red-500/60"}`}
                >
                  NO
                </button>
              </div>
            </div>
          );
        })}
        {state.consent_at && (
          <p className="text-[10px] text-zinc-600 text-center mt-3">
            Consensi acquisiti il {new Date(state.consent_at).toLocaleDateString("it-IT")}.
          </p>
        )}
      </div>
      <ConsentsHistory card={card} sessionToken={sessionToken} refreshKey={state.consent_updated_at} />
    </div>
  );
}

/** Storico delle variazioni dei consensi (trasparenza GDPR) + PDF scaricabile. */
function ConsentsHistory({ card, sessionToken, refreshKey }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    axios.get(`${API}/public/customer/${card}/consents/history`, { params: { session_token: sessionToken } }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [card, sessionToken, refreshKey]);
  if (!data) return null;
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");
  const lbl = (k) => (data.labels[k] || k).replace(" (necessario)", "");
  return (
    <div className="card-elev p-5" data-testid="public-consents-history">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-widest text-amber-300 flex items-center gap-2"><ShieldCheck className="h-3 w-3" /> Storico dei miei consensi</div>
        <a href={`${API}/public/customer/${card}/consents.pdf?session_token=${encodeURIComponent(sessionToken)}`} target="_blank" rel="noreferrer" className="pill pill-gold text-[10px]" data-testid="public-consents-pdf">Scarica PDF</a>
      </div>
      <div className="text-[11px] text-zinc-500 mt-2">Primo rilascio: <b className="text-zinc-300">{fmt(data.first_at)}</b> · Ultima modifica: <b className="text-zinc-300">{fmt(data.updated_at)}</b></div>
      <button type="button" className="text-xs text-amber-300 mt-2" onClick={() => setOpen((v) => !v)} data-testid="public-consents-history-toggle">{open ? "Nascondi" : "Mostra"} le {data.history.length} variazioni</button>
      {open && (
        <ul className="mt-2 space-y-1.5" data-testid="public-consents-history-list">
          {data.history.map((h, i) => (
            <li key={i} className="text-[11px] rounded-lg bg-zinc-950 border border-zinc-800 p-2">
              <div className="text-zinc-400">{fmt(h.at)} · {h.source}</div>
              <div className="text-zinc-200">{Object.entries(h.changes || {}).map(([k, v]) => `${lbl(k)}: ${v ? "SÌ" : "NO"}`).join(" · ") || "—"}</div>
              {h.note && <div className="text-zinc-500 italic">{h.note}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ChatPanel({ card, sessionToken }) {
  const [thread, setThread] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/public/messages/thread`, {
        params: { card_number: card, session_token: sessionToken },
      });
      setThread(data);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) { toast.error(formatErr(err)); }
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); /* eslint-disable-next-line */ }, []);

  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/public/messages/send`, {
        card_number: card, session_token: sessionToken, body: body.trim(),
      });
      setBody("");
      load();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="card-elev p-4 flex flex-col h-[70vh]" data-testid="public-chat-panel">
      <div className="font-heading font-semibold text-sm mb-3 flex items-center gap-2">
        <MessageCircle className="h-4 w-4 text-amber-300" /> Chat con il gestore
      </div>
      <div className="flex-1 overflow-y-auto space-y-2 pr-1">
        {thread.length === 0 && <div className="text-sm text-zinc-500 text-center py-6">Scrivi il primo messaggio!</div>}
        {thread.map((m) => {
          const mine = m.direction === "customer_to_shop";
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-2xl p-3 text-sm ${
                mine ? "bg-[color:var(--paprika)] text-white" : "bg-zinc-900 text-zinc-100"
              }`}>
                <div className="whitespace-pre-wrap">{m.body}</div>
                <div className="text-[10px] opacity-60 mt-1">{new Date(m.created_at).toLocaleString("it-IT")}</div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <div className="border-t border-zinc-800 pt-3 flex gap-2">
        <Textarea
          data-testid="public-chat-input"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={2}
          placeholder="Scrivi al gestore…"
          className="bg-zinc-950 border-zinc-800 resize-none"
          onKeyDown={(e) => (e.key === "Enter" && !e.shiftKey) && (e.preventDefault(), send())}
        />
        <Button
          data-testid="public-chat-send"
          onClick={send}
          disabled={busy || !body.trim()}
          className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)] shrink-0"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
